import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MedicationAdministrationRecord } from '@/components/participants/MedicationAdministrationRecord';   // BS3-013
import { PortalAccessTab } from '@/components/participants/PortalAccessTab';   // participant / family portal logins
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker, TrinitiTimePicker, TrinitiDateTimePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AISummaryPanel } from '@/components/shared/AISummaryPanel';
import { AuthedImage } from '@/components/shared/AuthedImage';
import { CareAndGoalsCard } from '@/components/participants/care-plan/CareAndGoalsCard';
import { SmartGoalWizard } from '@/components/participants/goals/SmartGoalWizard';
import { CarePlanTab as CarePlanTabV2 } from '@/components/participants/care-plan/CarePlanTab';
import { OutcomesReviewDialog } from '@/components/participants/outcomes/OutcomesReviewDialog';
import { OutcomeSummaryCard } from '@/components/participants/outcomes/OutcomeSummaryCard';
import { ParticipantOutcomesTab } from '@/components/participants/outcomes/ParticipantOutcomesTab';
import { CareSupportLanding } from '@/components/participants/care-support/CareSupportLanding';
import { ScheduleLanding } from '@/components/participants/schedule/ScheduleLanding';
import { ParticipantRostersTab } from '@/components/participants/schedule/ParticipantRostersTab';
import { ParticipantSchedulesTab } from '@/components/participants/schedule/ParticipantSchedulesTab';   // RP-3 Option B
import { ParticipantScheduleRows } from '@/components/participants/schedule/ParticipantScheduleRows';   // RP-3: shared table
import { runGenerate, schedulableLines } from '@/lib/scheduleHome';   // RP-3
import { ParticipantTransportTab } from '@/components/participants/schedule/ParticipantTransportTab';
import ParticipantTasksTab from '@/components/participants/ParticipantTasksTab';
import { ParticipantCalendarTab } from '@/components/participants/schedule/ParticipantCalendarTab';
import { ParticipantAppointmentOutcomesTab } from '@/components/participants/schedule/ParticipantAppointmentOutcomesTab';
import { PermissionAwareState } from '@/components/participants/schedule/PermissionAwareState';
import { NotesDocsLanding } from '@/components/participants/notes-docs/NotesDocsLanding';
import { AssessmentsIndexTab } from '@/components/participants/notes-docs/AssessmentsIndexTab';
import { ReportsIndexTab } from '@/components/participants/notes-docs/ReportsIndexTab';
import { DocumentCategoriesReference } from '@/components/participants/notes-docs/DocumentCategoriesReference';
import { ProgressSummaryPanel } from '@/components/ai/ProgressSummaryPanel';
import { ArrowLeft, Plus, DollarSign, Zap, User, FileText, Clock, Calendar, AlertTriangle, RefreshCw, Users, Printer, MapPin, Shield, UserCheck, Download, CheckCircle2, Eye, FileEdit } from 'lucide-react';
import { ParticipantEditDialog } from '@/components/participants/profile/ParticipantEditDialog';
import { toast } from 'sonner';
import { useAppDialog } from '@/hooks/useAppDialog';
// Participant Module Upgrade - Extended Tabs
import { ParticipantFundingReadinessPanel } from '@/components/participants/funding/ParticipantFundingReadinessPanel';
import { AddressesTab, SupportTeamTab, PreferencesRisksTab, NDISFundingDetailsTab, ConsentsTab, ExpiringItemsTab, DuplicateDetectionTab, LinkedModulesTab, OverviewSummaryCards, GoalsExtendedTab, TagsTab, FlagsTab, TimelineEventsTab } from './ParticipantExtendedTabs';
import { ParticipantChartsTab } from './charts/ParticipantChartsTab';
import { ParticipantDocumentsTab } from './documents/ParticipantDocumentsTab';
import { ParticipantAgreementsTab } from './agreements/ParticipantAgreementsTab';
import { RiskProfilePanel } from '@/components/participants/risk/RiskProfilePanel';
import { ReadinessCentreSummary } from '@/components/participants/readiness/ReadinessCentreSummary';
import { OnboardingChecklistPanel } from '@/components/participants/onboarding/OnboardingChecklistPanel';
import { SupportSnapshotCard } from '@/components/participants/support-snapshot/SupportSnapshotCard';
import { ParticipantHeaderV2 } from '@/components/participants/profile/ParticipantHeaderV2';
import { ParticipantOverviewV2 } from '@/components/participants/profile/overview/ParticipantOverviewV2';
import { ParticipantUnifiedTimeline } from '@/components/participants/timeline/ParticipantUnifiedTimeline';
import { CompactKpiRow } from '@/components/participants/profile/CompactKpiRow';
import { InlineTimelinePreview } from '@/components/participants/profile/InlineTimelinePreview';
import { ParticipantProfileV3Layout } from '@/components/participants/profile/ParticipantProfileV3Layout';
import { ParticipantOverviewDashboard } from '@/components/participants/ParticipantOverviewDashboard';
// Phase 2 — Accommodation depth + Communication logs (additive, feature-flag gated)
import { AccommodationOverlayTab } from '@/components/participants/accommodation/AccommodationOverlayTab';
import { CurrentResidenceTab } from '@/components/participants/accommodation/CurrentResidenceTab';
import { EmailLogTab, SmsLogTab, EngagementThreadsTab } from '@/components/participants/communication/CommunicationLogTabs';
// Phase 3 — Clinical (Allergies + Health Conditions)
import { AllergiesTab } from '@/components/participants/clinical/AllergiesTab';
import { HealthConditionsTab } from '@/components/participants/clinical/HealthConditionsTab';
import { useAuth } from '@/lib/auth';

const dayNames = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

// Define ClipboardCheck component before using it
const ClipboardCheck = ({ className }) => <FileText className={className} />;

const TIMELINE_ICONS = {
  funding_plan: DollarSign, funding_transaction: DollarSign,
  shift: Calendar, shift_cancellation: AlertTriangle,
  timesheet: ClipboardCheck, invoice: FileText,
  progress_note: FileText, claim_batch: DollarSign, claim_line: DollarSign,
  audit: Clock, incident: AlertTriangle,
};

import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FREQUENCY_OPTIONS, WEEKDAYS, NTH_OPTIONS,
  buildRulePayload, validateRuleForm, buildGeneratePayload, validateGenerateForm, GENERATED_SHIFT_RATIOS,
} from '@/lib/recurrence';
import { ProgressNoteReadOnlyDetail } from '@/components/participants/notes/ProgressNoteReadOnlyDetail';
import { ProgressNoteForm, emptyNoteForm } from '@/components/participants/notes/ProgressNoteForm';
import { HandoverReadOnlyDetail } from '@/components/participants/notes/HandoverReadOnlyDetail';
import { NOTE_TEMPLATES, NOTE_STATUSES, DEFAULT_NOTE_FILTERS, isDateRangeInvalid, activeNoteFilterCount, filterProgressNotes, noteAuthorOptions, sortProgressNotes } from '@/components/participants/notes/progressNoteFilters';
import { getBrandName } from '@/lib/brandName';

function ContinuityTab({ participantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/roster/intelligence/participants/${participantId}/continuity?days=90`)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) return <p className="text-muted-foreground py-4">Loading continuity data...</p>;
  if (!data) return <p className="text-muted-foreground py-4">No continuity data available</p>;

  const levelColor = data.continuityLevel === 'GOOD' ? 'text-green-600 bg-green-50 border-green-200' : data.continuityLevel === 'MODERATE' ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';

  return (
    <div className="space-y-4" data-testid="continuity-tab">
      {/* Score Card */}
      <Card className={`border ${levelColor.split(' ').slice(2).join(' ')}`}>
        <CardContent className="py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Continuity Score (last {data.periodDays} days)</p>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-4xl font-bold ${levelColor.split(' ')[0]}`}>{data.continuityScore}%</span>
                <Badge className={levelColor.split(' ').slice(0, 2).join(' ')}>{data.continuityLevel}</Badge>
              </div>
            </div>
            <div className="text-right text-sm">
              <p>{data.totalShifts} total shifts</p>
              <p className="text-muted-foreground">{data.uniqueStaffCount} different staff</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary Staff */}
      {data.primaryStaff && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Primary Support Worker</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-lg">{data.primaryStaff.staffName}</p>
                <p className="text-sm text-muted-foreground">{data.primaryStaff.shiftCount} shifts ({data.primaryStaff.percentage}%)</p>
              </div>
              <RefreshCw className={`w-8 h-8 ${data.continuityLevel === 'GOOD' ? 'text-green-500' : data.continuityLevel === 'MODERATE' ? 'text-amber-500' : 'text-red-500'}`} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Breakdown */}
      {data.staffBreakdown?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recent Support Workers</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead className="text-right">Shifts</TableHead><TableHead className="text-right">Coverage</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.staffBreakdown.map((s, i) => (
                  <TableRow key={s.staffId}>
                    <TableCell className="font-medium">{s.staffName}{i === 0 && <Badge className="ml-2 bg-green-500 text-white text-[10px]">Primary</Badge>}</TableCell>
                    <TableCell className="text-right">{s.shiftCount}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{width: `${Math.min(s.percentage, 100)}%`}} /></div>
                        <span className="text-xs w-10 text-right">{s.percentage}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const RELATIONSHIPS = ['FAMILY', 'GUARDIAN', 'NOMINEE', 'SUPPORT_COORDINATOR', 'CASE_MANAGER', 'PLAN_MANAGER', 'LAC', 'DOCTOR', 'SPECIALIST', 'THERAPIST', 'OTHER'];
const CHANNELS = ['PORTAL', 'SMS', 'EMAIL', 'PHONE'];
const POA_TYPES = ['FINANCIAL', 'MEDICAL', 'GENERAL', 'ENDURING'];

function ContactsTab({ participantId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/engagement/contacts/by-participant/${participantId}`)
      .then(r => setContacts(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/engagement/contacts/${editing.id}`, form);
        toast.success('Contact updated');
      } else {
        await api.post('/engagement/contacts', { ...form, participantId });
        toast.success('Contact created');
      }
      setShowForm(false); setEditing(null); load();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const handleDeactivate = async (id) => {
    try { await api.post(`/engagement/contacts/${id}/deactivate`); toast.success('Deactivated'); load(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  return (
    <div className="space-y-4" data-testid="contacts-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold">Participant Contacts</h3>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-contact-btn"><Plus className="w-3 h-3 mr-1" />Add Contact</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : contacts.length === 0 ? <p className="text-muted-foreground text-center py-6">No contacts</p> : (
        <div className="space-y-2">
          {contacts.map(c => (
            <Card key={c.id} className={c.status === 'INACTIVE' ? 'opacity-50' : ''}>
              <CardContent className="py-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{c.firstName} {c.lastName}</p>
                      <Badge variant="outline">{c.relationship?.replace(/_/g, ' ')}</Badge>
                      {c.relationshipDetail && <span className="text-xs text-muted-foreground">({c.relationshipDetail})</span>}
                      <Badge variant="secondary">{c.preferredChannel}</Badge>
                      {c.status === 'INACTIVE' && <Badge variant="destructive">Inactive</Badge>}
                      {c.isEmergencyContact && <Badge variant="destructive" className="text-[10px]">Emergency</Badge>}
                      {c.isPrimaryContact && <Badge className="bg-blue-500 text-white text-[10px]">Primary</Badge>}
                      {c.isDecisionMaker && <Badge className="bg-purple-500 text-white text-[10px]">Decision Maker</Badge>}
                      {c.hasPowerOfAttorney && <Badge className="bg-amber-500 text-white text-[10px]">POA ({c.poaType})</Badge>}
                      {c.authorityType && <Badge data-testid={`contact-authority-badge-${c.id}`} className={`${c.authorityStatus === 'VERIFIED' ? 'bg-emerald-600' : 'bg-slate-500'} text-white text-[10px]`}>{c.authorityStatus === 'VERIFIED' ? 'Verified authority' : `Authority ${String(c.authorityStatus || 'claimed').toLowerCase()}`}: {AUTHORITY_TYPES[c.authorityType] || c.authorityType}</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 space-x-3">
                      {c.email && <span>{c.email}</span>}
                      {c.phone && <span>{c.phone}</span>}
                      {c.phoneWork && <span>Work: {c.phoneWork}</span>}
                      {c.organization && <span className="italic">{c.organization}</span>}
                    </div>
                    {(c.addressLine1 || c.suburb) && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {[c.addressLine1, c.addressLine2, c.suburb, c.state, c.postcode].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {c.canReceiveGeneralUpdates && <Badge variant="outline" className="text-[10px]">General</Badge>}
                      {c.canReceiveScheduleChanges && <Badge variant="outline" className="text-[10px]">Schedule</Badge>}
                      {c.canReceiveMedicalUpdates && <Badge variant="outline" className="text-[10px]">Medical</Badge>}
                      {c.canReceiveIncidentNotifications && <Badge variant="outline" className="text-[10px]">Incidents</Badge>}
                      {c.canReceiveBillingUpdates && <Badge variant="outline" className="text-[10px]">Billing</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`edit-contact-${c.id}`}>Edit</Button>
                    {c.status === 'ACTIVE' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeactivate(c.id)} data-testid={`deactivate-contact-${c.id}`}>Deactivate</Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Contact' : 'Add Contact'}</DialogTitle></DialogHeader>
          <ContactForm contact={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AUTHORITY_TYPES = { GUARDIAN: 'Guardian (tribunal-appointed)', PLAN_NOMINEE: 'NDIS plan nominee', ENDURING_POWER_OF_ATTORNEY: 'Enduring power of attorney', MEDICAL_AUTHORITY: 'Medical decision-making authority', FINANCIAL_ADMINISTRATOR: 'Financial administrator', OTHER: 'Other authority' };

function ContactForm({ contact, onSave, onCancel }) {
  const [form, setForm] = useState(contact ? {
    firstName: contact.firstName, lastName: contact.lastName, relationship: contact.relationship,
    relationshipDetail: contact.relationshipDetail || '',
    email: contact.email || '', phone: contact.phone || '', phoneWork: contact.phoneWork || '',
    // FRD Section 6.4 - Address fields
    addressLine1: contact.addressLine1 || '', addressLine2: contact.addressLine2 || '',
    suburb: contact.suburb || '', state: contact.state || '',
    postcode: contact.postcode || '', country: contact.country || 'Australia',
    address: contact.address || '', // Legacy field
    organization: contact.organization || '',
    preferredChannel: contact.preferredChannel,
    canReceiveGeneralUpdates: contact.canReceiveGeneralUpdates, canReceiveScheduleChanges: contact.canReceiveScheduleChanges,
    canReceiveMedicalUpdates: contact.canReceiveMedicalUpdates, canReceiveIncidentNotifications: contact.canReceiveIncidentNotifications,
    canReceiveBillingUpdates: contact.canReceiveBillingUpdates,
    isEmergencyContact: contact.isEmergencyContact || false,
    isPrimaryContact: contact.isPrimaryContact || false,
    isDecisionMaker: contact.isDecisionMaker || false,
    hasPowerOfAttorney: contact.hasPowerOfAttorney || false,
    poaType: contact.poaType || '',
    poaDocumentId: contact.poaDocumentId || '',
    authorityType: contact.authorityType || '', authorityStatus: contact.authorityStatus || '', authorityEvidenceDocumentId: contact.authorityEvidenceDocumentId || '',
    authorityFrom: contact.authorityFrom || '', authorityTo: contact.authorityTo || '', authorityOtherDescription: contact.authorityOtherDescription || '',
    portalUserId: contact.portalUserId || '', notes: contact.notes || '',
  } : {
    firstName: '', lastName: '', relationship: 'FAMILY', relationshipDetail: '',
    email: '', phone: '', phoneWork: '',
    addressLine1: '', addressLine2: '', suburb: '', state: '', postcode: '', country: 'Australia',
    address: '', organization: '',
    preferredChannel: 'EMAIL',
    canReceiveGeneralUpdates: true, canReceiveScheduleChanges: true, canReceiveMedicalUpdates: false,
    canReceiveIncidentNotifications: false, canReceiveBillingUpdates: false,
    isEmergencyContact: false, isPrimaryContact: false, isDecisionMaker: false,
    hasPowerOfAttorney: false, poaType: '', poaDocumentId: '',
    authorityType: '', authorityStatus: '', authorityEvidenceDocumentId: '', authorityFrom: '', authorityTo: '', authorityOtherDescription: '',
    portalUserId: '', notes: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>First Name</Label><Input data-testid="contact-first-name" value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
        <div><Label>Last Name</Label><Input data-testid="contact-last-name" value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Relationship</Label>
          <Select value={form.relationship} onValueChange={v => set('relationship', v)}>
            <SelectTrigger data-testid="contact-relationship"><SelectValue /></SelectTrigger>
            <SelectContent>{RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Relationship Detail</Label><Input value={form.relationshipDetail} onChange={e => set('relationshipDetail', e.target.value)} placeholder="e.g., Mother, GP" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Email</Label><Input data-testid="contact-email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
        <div><Label>Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Work Phone</Label><Input value={form.phoneWork} onChange={e => set('phoneWork', e.target.value)} /></div>
        <div><Label>Organization</Label><Input value={form.organization} onChange={e => set('organization', e.target.value)} placeholder="For professional contacts" /></div>
      </div>
      
      {/* FRD Section 6.4 - Full Address Fields */}
      <div className="p-3 border rounded-md space-y-3 bg-muted/20">
        <Label className="font-semibold">Address Details</Label>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Address Line 1</Label><Input value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} placeholder="Street address" /></div>
          <div><Label className="text-xs">Address Line 2</Label><Input value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} placeholder="Unit, apt, etc." /></div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div><Label className="text-xs">Suburb</Label><Input value={form.suburb} onChange={e => set('suburb', e.target.value)} /></div>
          <div><Label className="text-xs">State</Label>
            <Select value={form.state} onValueChange={v => set('state', v)}>
              <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Postcode</Label><Input value={form.postcode} onChange={e => set('postcode', e.target.value)} maxLength={4} /></div>
          <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={e => set('country', e.target.value)} /></div>
        </div>
      </div>
      
      <div><Label>Preferred Channel</Label>
        <Select value={form.preferredChannel} onValueChange={v => set('preferredChannel', v)}>
          <SelectTrigger data-testid="contact-channel"><SelectValue /></SelectTrigger>
          <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Key Contact Flags */}
      <div className="p-3 border rounded-md space-y-2 bg-muted/30">
        <Label className="font-semibold">Contact Flags</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['isEmergencyContact', 'Emergency Contact'],
            ['isPrimaryContact', 'Primary Contact'],
            ['isDecisionMaker', 'Decision Maker'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="rounded" />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Power of Attorney */}
      <div className="p-3 border rounded-md space-y-2 bg-muted/30">
        <label className="flex items-center gap-2 text-sm cursor-pointer font-semibold">
          <input type="checkbox" checked={form.hasPowerOfAttorney} onChange={e => set('hasPowerOfAttorney', e.target.checked)} className="rounded" />
          Has Power of Attorney
        </label>
        {form.hasPowerOfAttorney && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><Label className="text-xs">POA Type</Label>
              <Select value={form.poaType} onValueChange={v => set('poaType', v)}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{POA_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">POA Document ID</Label><Input value={form.poaDocumentId} onChange={e => set('poaDocumentId', e.target.value)} placeholder="Link to document" /></div>
          </div>
        )}
      </div>

      {/* Day 24F-C — verified legal authority (explicit; never inferred from relationship) */}
      <div className="rounded-md border p-3 space-y-2" data-testid="contact-authority-section">
        <Label className="block">Legal authority to act for the participant</Label>
        <p className="text-xs text-muted-foreground">Relationship (above) is not legal authority. Representative signing is only available when an authority type is recorded and its status is <strong>Verified</strong>.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Authority type</Label>
            <Select value={form.authorityType || 'NONE'} onValueChange={v => set('authorityType', v === 'NONE' ? '' : v)}>
              <SelectTrigger data-testid="contact-authority-type"><SelectValue placeholder="None recorded" /></SelectTrigger>
              <SelectContent><SelectItem value="NONE">None recorded</SelectItem>{Object.entries(AUTHORITY_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Authority status</Label>
            <Select value={form.authorityStatus || 'CLAIMED'} onValueChange={v => set('authorityStatus', v)} disabled={!form.authorityType}>
              <SelectTrigger data-testid="contact-authority-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="CLAIMED">Claimed (not yet verified)</SelectItem><SelectItem value="VERIFIED">Verified</SelectItem><SelectItem value="ENDED">Ended</SelectItem></SelectContent>
            </Select>
          </div>
          {form.authorityType === 'OTHER' && <div className="sm:col-span-2"><Label className="text-xs">Describe the authority</Label><Input value={form.authorityOtherDescription} onChange={e => set('authorityOtherDescription', e.target.value)} data-testid="contact-authority-other" /></div>}
          {form.authorityType && <>
            <div><Label className="text-xs">Effective from</Label><Input type="date" value={form.authorityFrom} onChange={e => set('authorityFrom', e.target.value)} data-testid="contact-authority-from" /></div>
            <div><Label className="text-xs">Effective to</Label><Input type="date" value={form.authorityTo} onChange={e => set('authorityTo', e.target.value)} data-testid="contact-authority-to" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Evidence document reference</Label><Input value={form.authorityEvidenceDocumentId} onChange={e => set('authorityEvidenceDocumentId', e.target.value)} placeholder="Document ID of the order / instrument" data-testid="contact-authority-evidence" /></div>
          </>}
        </div>
      </div>

      {/* Communication Preferences */}
      <div>
        <Label className="mb-2 block">Communication Preferences</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['canReceiveGeneralUpdates', 'General Updates'],
            ['canReceiveScheduleChanges', 'Schedule Changes'],
            ['canReceiveMedicalUpdates', 'Medical Updates'],
            ['canReceiveIncidentNotifications', 'Incident Notifications'],
            ['canReceiveBillingUpdates', 'Billing Updates'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="rounded" />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div><Label>Portal User ID (optional)</Label><Input data-testid="contact-portal-user" value={form.portalUserId} onChange={e => set('portalUserId', e.target.value)} placeholder="Link to existing portal user" /></div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes..." /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="save-contact-btn" onClick={() => onSave(form)} disabled={!form.firstName || !form.lastName}>Save</Button>
      </div>
    </div>
  );
}

const APT_TYPES = ['GP', 'ALLIED_HEALTH', 'THERAPY', 'FAMILY_MEETING', 'NDIS_REVIEW', 'CASE_CONFERENCE', 'TRANSPORT', 'OTHER'];
const APT_STATUS_COLORS = { SCHEDULED: 'bg-blue-500 text-white', CONFIRMED: 'bg-green-500 text-white', COMPLETED: 'bg-slate-500 text-white', CANCELLED: 'bg-red-500 text-white' };

function GroupSessionsTab({ participantId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/group-sessions/participant/${participantId}/sessions`);
        setSessions(res.data.data || []);
      } catch { setSessions([]); }
      finally { setLoading(false); }
    };
    load();
  }, [participantId]);

  const STATUS_COLORS = { draft: 'bg-slate-100 text-slate-700', published: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700', completed: 'bg-emerald-100 text-emerald-700' };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="group-sessions-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Linked Group Sessions ({sessions.length})</h3>
      </div>
      {sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No group sessions linked to this participant.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <Card key={s.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-indigo-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.session_code} &middot; {s.session_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span>{s.start_datetime ? new Date(s.start_datetime).toLocaleDateString() : ''}</span>
                    <span className="text-muted-foreground">{s.start_datetime ? new Date(s.start_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}</span>
                    <Badge className={STATUS_COLORS[s.status] || 'bg-slate-100'}>{s.status}</Badge>
                    <span className="font-mono">{s.staff_count || 0}S / {s.participant_count || 0}P</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AppointmentsTab({ participantId }) {
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fetchState, setFetchState] = useState('ok'); // 'ok' | 'empty' | 'denied' | 'error'

  const load = useCallback(() => {
    setLoading(true);
    setFetchState('ok');
    api.get(`/appointments?participantId=${participantId}&pageSize=50`)
      .then(r => {
        const items = r.data.items || [];
        setApts(items);
        setFetchState(items.length === 0 ? 'empty' : 'ok');
      })
      .catch(err => {
        const status = err?.response?.status ?? null;
        // Preserve original HTTP status for logging (do NOT suppress).
        console.error('[AppointmentsTab] fetch failed', { httpStatus: status });
        if (status === 401 || status === 403) {
          setFetchState('denied');
        } else {
          setFetchState('error');
        }
        setApts([]);
      })
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'cancel') {
        await api.post(`/appointments/${id}/cancel`, { reason: 'Cancelled from UI' });
      } else {
        await api.post(`/appointments/${id}/${action}`);
      }
      toast.success(`Appointment ${action}ed`);
      load();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/appointments/${editing.id}`, form);
      } else {
        await api.post('/appointments', { ...form, participantId });
      }
      toast.success(editing ? 'Updated' : 'Created');
      setShowForm(false); setEditing(null); load();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/appointments/export-csv?participantId=${participantId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'appointments.csv'; a.click(); window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) { notify.error(e, 'EXPORT_FAILED'); }
  };

  return (
    <div className="space-y-4" data-testid="appointments-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold">Appointments</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-appointments-btn">Export CSV</Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-appointment-btn"><Plus className="w-3 h-3 mr-1" />New Appointment</Button>
        </div>
      </div>
      {loading ? <p className="text-muted-foreground" data-testid="appointments-state-loading">Loading...</p> : (
        fetchState === 'denied' ? (
          <PermissionAwareState status="denied" testIdBase="appointments" />
        ) : fetchState === 'error' ? (
          <PermissionAwareState status="error" testIdBase="appointments" onRetry={load} />
        ) : fetchState === 'empty' ? (
          <PermissionAwareState status="empty" testIdBase="appointments" emptyText="No appointments found for this participant." />
        ) : (
        <div className="space-y-2">
          {apts.map(a => (
            <Card key={a.id}>
              <CardContent className="py-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={APT_STATUS_COLORS[a.status] || ''}>{a.status}</Badge>
                      <span className="font-mono text-xs">{a.appointmentNo}</span>
                      <Badge variant="outline">{a.appointmentType}</Badge>
                    </div>
                    <p className="font-medium mt-1">{a.title}</p>
                    <div className="text-xs text-muted-foreground mt-1 space-x-3">
                      <span>{a.scheduledStart?.replace('T', ' ')}</span>
                      {a.location && <span>{a.location}</span>}
                      {a.providerName && <span>{a.providerName}</span>}
                    </div>
                    <div className="flex gap-1 mt-1">
                      {a.transportRequired && <Badge variant="outline" className="text-[10px]">Transport</Badge>}
                      {a.staffRequired && <Badge variant="outline" className="text-[10px]">Staff</Badge>}
                      {a.assignedStaffName && <Badge variant="secondary" className="text-[10px]">{a.assignedStaffName}</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {a.status === 'SCHEDULED' && <Button size="sm" variant="outline" onClick={() => handleAction(a.id, 'confirm')}>Confirm</Button>}
                    {(a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && <Button size="sm" variant="outline" onClick={() => handleAction(a.id, 'complete')}>Complete</Button>}
                    {a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleAction(a.id, 'cancel')}>Cancel</Button>}
                    {a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setShowForm(true); }}>Edit</Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )
      )}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Appointment' : 'New Appointment'}</DialogTitle></DialogHeader>
          <AppointmentForm appointment={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppointmentForm({ appointment, onSave, onCancel }) {
  const [form, setForm] = useState(appointment ? {
    title: appointment.title, appointmentType: appointment.appointmentType,
    scheduledStart: appointment.scheduledStart || '', scheduledEnd: appointment.scheduledEnd || '',
    location: appointment.location || '', providerName: appointment.providerName || '',
    contactDetails: appointment.contactDetails || '', transportRequired: appointment.transportRequired || false,
    staffRequired: appointment.staffRequired || false, assignedStaffId: appointment.assignedStaffId || '', notes: appointment.notes || '',
  } : {
    title: '', appointmentType: 'GP', scheduledStart: '', scheduledEnd: '',
    location: '', providerName: '', contactDetails: '', transportRequired: false,
    staffRequired: false, assignedStaffId: '', notes: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3">
      <div><Label>Title</Label><Input data-testid="apt-title" value={form.title} onChange={e => set('title', e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label>
          <Select value={form.appointmentType} onValueChange={v => set('appointmentType', v)}>
            <SelectTrigger data-testid="apt-type"><SelectValue /></SelectTrigger>
            <SelectContent>{APT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Provider</Label><Input value={form.providerName} onChange={e => set('providerName', e.target.value)} placeholder="Dr. Smith" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start</Label><TrinitiDateTimePicker data-testid="apt-start" value={form.scheduledStart} onChange={v => set('scheduledStart', v)} testId="apt-start" /></div>
        <div><Label>End</Label><TrinitiDateTimePicker data-testid="apt-end" value={form.scheduledEnd} onChange={v => set('scheduledEnd', v)} testId="apt-end" /></div>
      </div>
      <div><Label>Location</Label><Input value={form.location} onChange={e => set('location', e.target.value)} /></div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.transportRequired} onChange={e => set('transportRequired', e.target.checked)} className="rounded" />Transport Required</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.staffRequired} onChange={e => set('staffRequired', e.target.checked)} className="rounded" />Staff Required</label>
      </div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="save-apt-btn" onClick={() => onSave(form)} disabled={!form.title || !form.scheduledStart}>Save</Button>
      </div>
    </div>
  );
}

function NotesTab({ participantId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_NOTE_FILTERS);
  const [sort, setSort] = useState('NEWEST');
  // Add-note state (admin can attach a note to an in-progress shift for this participant)
  const [createOpen, setCreateOpen] = useState(false);
  const [eligibleShifts, setEligibleShifts] = useState([]);
  const [shiftId, setShiftId] = useState('');
  const [noteForm, setNoteForm] = useState(emptyNoteForm());
  const [busy, setBusy] = useState(false);

  const loadNotes = useCallback(async () => {
    setLoading(true); setError('');
    try {
      let page = 1, all = [], total = Infinity;
      while (all.length < total && page <= 100) {
        const r = await api.get(`/notes/by-participant/${participantId}?page=${page}&pageSize=100`);
        const items = r.data.items || r.data.data || [];
        total = typeof r.data.total === 'number' ? r.data.total : items.length;
        all = all.concat(items);
        if (items.length === 0) break;
        page += 1;
      }
      setNotes(all);
    } catch {
      setError('Unable to load progress notes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const openCreate = async () => {
    setNoteForm(emptyNoteForm()); setShiftId(''); setEligibleShifts([]);
    setCreateOpen(true);
    try {
      // Only shifts currently IN PROGRESS for this participant are eligible.
      // Note: the roster endpoint expects the `participantId` alias.
      const r = await api.get('/roster/shifts', { params: { participantId: participantId, status_filter: 'IN_PROGRESS', pageSize: 100 } });
      const items = r.data.items || r.data.data || [];
      const inProgress = items.filter((s) =>
        String(s.participant_id) === String(participantId)
        && (String(s.status || '').toUpperCase() === 'IN_PROGRESS' || (s.check_in_time && !s.check_out_time)));
      setEligibleShifts(inProgress);
      if (inProgress.length === 1) setShiftId(inProgress[0].id);
    } catch { /* selector will show none */ }
  };

  const createNote = async (submitAfter) => {
    if (!shiftId) { toast.error('Select an in-progress shift for this participant'); return; }
    if (!noteForm.note?.trim()) { toast.error('Note is required'); return; }
    setBusy(true);
    try {
      const res = await api.post('/notes', { shift_id: shiftId, participant_id: participantId, ...noteForm });
      if (submitAfter) {
        await api.post(`/notes/${res.data.data.id}/submit`);
        toast.success('Note submitted');
      } else {
        toast.success('Note saved as draft');
      }
      setCreateOpen(false);
      await loadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.error?.message || e?.response?.data?.detail || 'Failed to save note');
    } finally { setBusy(false); }
  };

  const dateInvalid = isDateRangeInvalid(filters);
  const activeCount = activeNoteFilterCount(filters);
  const authorOptions = useMemo(() => noteAuthorOptions(notes), [notes]);
  const filtered = useMemo(
    () => (dateInvalid ? [] : sortProgressNotes(filterProgressNotes(notes, filters), sort)),
    [notes, filters, sort, dateInvalid]
  );
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters(DEFAULT_NOTE_FILTERS);

  return (
    <Card>
      <div className="flex flex-wrap items-end gap-3 p-4 border-b" data-testid="notes-filter-bar">
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => set('status', v)}>
            <SelectTrigger className="h-9 w-[140px] text-sm" data-testid="notes-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {NOTE_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Type</Label>
          <Select value={filters.template} onValueChange={(v) => set('template', v)}>
            <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="notes-filter-template"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All types</SelectItem>
              {NOTE_TEMPLATES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <TrinitiDatePicker className="h-9 w-[150px] text-sm" value={filters.from} maxDate={filters.to ? new Date(filters.to + 'T00:00:00') : undefined} onChange={(v) => set('from', v)} testId="notes-filter-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <TrinitiDatePicker className="h-9 w-[150px] text-sm" value={filters.to} minDate={filters.from ? new Date(filters.from + 'T00:00:00') : undefined} onChange={(v) => set('to', v)} testId="notes-filter-to" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Requires Follow-up</Label>
          <Select value={filters.followUp} onValueChange={(v) => set('followUp', v)}>
            <SelectTrigger className="h-9 w-[120px] text-sm" data-testid="notes-filter-followup"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="YES">Yes</SelectItem>
              <SelectItem value="NO">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Author</Label>
          <Select value={filters.author} onValueChange={(v) => set('author', v)}>
            <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="notes-filter-author"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All authors</SelectItem>
              {authorOptions.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sort</Label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="notes-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NEWEST">Newest first</SelectItem>
              <SelectItem value="OLDEST">Oldest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {activeCount > 0 && <Badge variant="secondary" data-testid="notes-active-filter-count">{activeCount} filter{activeCount > 1 ? 's' : ''} active</Badge>}
          <Button variant="outline" size="sm" className="h-9" onClick={clearFilters} disabled={activeCount === 0} data-testid="notes-clear-filters">Clear filters</Button>
          <Button size="sm" className="h-9" onClick={openCreate} data-testid="notes-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />New Note</Button>
        </div>
        {dateInvalid && <p className="w-full text-xs text-destructive" data-testid="notes-filter-date-error">"From" date must be on or before "To" date.</p>}
      </div>
      {!loading && !error && notes.length > 0 && (
        <div className="px-4 py-2 text-xs text-muted-foreground border-b" data-testid="notes-result-count">
          {activeCount > 0
            ? `Showing ${filtered.length} of ${notes.length} progress note${notes.length > 1 ? 's' : ''}`
            : `${notes.length} progress note${notes.length > 1 ? 's' : ''}`}
        </div>
      )}
      <CardContent className="p-0">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Content</TableHead><TableHead>Author</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="notes-loading">Loading notes…</TableCell></TableRow>
          : error ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-destructive" data-testid="notes-error">{error}</TableCell></TableRow>
          : notes.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="notes-empty">No progress notes recorded.</TableCell></TableRow>
          : filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="notes-filtered-empty">No progress notes match these filters.</TableCell></TableRow>
          : filtered.map(n => (
            <TableRow key={n.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => setSelected(n)} data-testid={`note-row-${n.id}`}>
              <TableCell className="font-mono text-xs">{n.created_at?.slice(0,10)}</TableCell>
              <TableCell><StatusBadge status={n.note_type || n.template_key || '-'} /></TableCell>
              <TableCell className="font-medium max-w-xs truncate">{n.content || n.note || n.title || '-'}</TableCell>
              <TableCell className="text-sm">{n.author_name || 'N/A'}</TableCell>
              <TableCell><StatusBadge status={n.status} /></TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); setSelected(n); }} data-testid={`note-view-${n.id}`}>
                  <Eye className="h-3.5 w-3.5 mr-1" /> View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="note-detail-dialog">
          <DialogHeader>
            <DialogTitle>Progress Note</DialogTitle>
            <DialogDescription>Read-only view of the recorded progress note.</DialogDescription>
          </DialogHeader>
          {selected && <ProgressNoteReadOnlyDetail note={selected} />}
        </DialogContent>
      </Dialog>

      {/* Create progress note (admin) — gated to an in-progress shift */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="note-create-dialog">
          <DialogHeader>
            <DialogTitle>New progress note</DialogTitle>
            <DialogDescription>Attach a progress note to a shift that is currently in progress for this participant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Shift *</Label>
              {eligibleShifts.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="note-create-no-shift">You can only add a progress note while a shift for this participant is in progress (checked in).</p>
              ) : (
                <Select value={shiftId} onValueChange={setShiftId}>
                  <SelectTrigger className="h-9 text-sm" data-testid="note-create-shift-select"><SelectValue placeholder="Select shift" /></SelectTrigger>
                  <SelectContent>
                    {eligibleShifts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {`${s.date || ''} ${s.start_time ? `· ${s.start_time}${s.end_time ? `–${s.end_time}` : ''}` : ''} ${s.status ? `· ${s.status}` : ''}`.trim()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <ProgressNoteForm form={noteForm} onChange={setNoteForm} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button variant="outline" onClick={() => createNote(false)} disabled={busy || !shiftId || !noteForm.note?.trim()} data-testid="note-create-save-draft">Save Draft</Button>
              <Button onClick={() => createNote(true)} disabled={busy || !shiftId || !noteForm.note?.trim()} data-testid="note-create-save-submit">{busy ? 'Saving…' : 'Save & Submit'}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}

function AdminHandoverTab({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    api.get(`/handover/by-participant/${participantId}`)
      .then(r => { if (alive) setItems(Array.isArray(r.data?.data) ? r.data.data : (r.data?.items || [])); })
      .catch(() => { if (alive) setError('Unable to load handover notes. Please try again.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [participantId]);
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Summary</TableHead><TableHead>Acknowledged</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="handover-loading">Loading handover notes…</TableCell></TableRow>
          : error ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-destructive" data-testid="handover-error">{error}</TableCell></TableRow>
          : items.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="handover-empty">No handover notes for this participant.</TableCell></TableRow>
          : items.map(h => {
            const acks = h.acknowledgements || [];
            return (
              <TableRow key={h.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => setSelected(h)} data-testid={`handover-row-${h.id}`}>
                <TableCell className="font-mono text-xs">{h.created_at?.slice(0,10)}</TableCell>
                <TableCell className="font-medium max-w-md truncate">{h.handover_summary || '-'}</TableCell>
                <TableCell>{acks.length > 0 ? <Badge variant="secondary">Acknowledged ({acks.length})</Badge> : <Badge variant="outline">No</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); setSelected(h); }} data-testid={`handover-view-${h.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="handover-detail-dialog">
          <DialogHeader>
            <DialogTitle>Handover Note</DialogTitle>
            <DialogDescription>Read-only view of the recorded handover note.</DialogDescription>
          </DialogHeader>
          {selected && <HandoverReadOnlyDetail handover={selected} />}
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
}


function FormsTab({ participantId }) {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ template_id: '', due_date: '', priority: 'NORMAL' });
  const navigate = useNavigate();

  const loadAssignments = useCallback(() => {
    api.get(`/forms/assignments?target_type=PARTICIPANT&target_id=${participantId}&pageSize=50`)
      .then(r => setAssignments(r.data.items || []))
      .catch(() => {});
  }, [participantId]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  useEffect(() => {
    if (showAssign) {
      api.get('/forms/templates?status=PUBLISHED&pageSize=100').then(r => setTemplates(r.value?.data?.items || r.data?.items || [])).catch(() => {});
    }
  }, [showAssign]);

  const handleAssign = async () => {
    if (!assignForm.template_id) { notify.warn('VALIDATION_REQUIRED'); return; }
    try {
      await api.post('/forms/assignments', {
        template_id: assignForm.template_id,
        target_type: 'PARTICIPANT',
        target_id: participantId,
        due_date: assignForm.due_date || null,
        priority: assignForm.priority,
      });
      toast.success('Form assigned');
      setShowAssign(false);
      setAssignForm({ template_id: '', due_date: '', priority: 'NORMAL' });
      loadAssignments();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const STATUS_COLORS = { PENDING: 'secondary', IN_PROGRESS: 'default', SUBMITTED: 'default', APPROVED: 'default', REJECTED: 'destructive' };

  return (
    <div className="space-y-4" data-testid="forms-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Assigned Forms</h3>
        <Button size="sm" onClick={() => setShowAssign(true)} data-testid="assign-form-to-participant-btn">
          <Plus className="w-4 h-4 mr-1" />Assign Form
        </Button>
      </div>

      {assignments.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No forms assigned to this participant yet</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Card key={a.id} className="hover:shadow-sm transition-shadow" data-testid={`participant-form-${a.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => navigate(`/forms/assignments/${a.id}/fill`)}>
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{a.template_name || 'Form'}</p>
                    <p className="text-xs text-muted-foreground">
                      Priority: {a.priority}{a.due_date ? ` · Due: ${a.due_date}` : ''}{a.assigned_at ? ` · Assigned: ${a.assigned_at.slice(0,10)}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/forms/assignments/${a.id}/print`); }} data-testid={`print-form-${a.id}`}>
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Badge variant={STATUS_COLORS[a.status] || 'secondary'}>{a.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Form</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Form Template *</Label>
              <Select value={assignForm.template_id} onValueChange={v => setAssignForm(p => ({ ...p, template_id: v }))}>
                <SelectTrigger data-testid="participant-assign-template"><SelectValue placeholder="Select a form..." /></SelectTrigger>
                <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due Date</Label>
                <TrinitiDatePicker value={assignForm.due_date} onChange={v => setAssignForm(p => ({ ...p, due_date: v }))} testId="assign-due-date" />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={assignForm.priority} onValueChange={v => setAssignForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} data-testid="confirm-participant-assign">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamsTab({ participantId }) {
  const [teams, setTeams] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/teams/by-participant/${participantId}`).then(r => setTeams(r.data.items || [])).catch(() => {});
  }, [participantId]);
  return (
    <div className="space-y-4" data-testid="teams-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Linked Teams</h3>
      </div>
      {teams.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground"><Users className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">No teams linked to this participant</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {teams.map(t => (
            <Card key={t.id} className="hover:shadow-sm cursor-pointer" onClick={() => navigate(`/teams/${t.id}`)} data-testid={`participant-team-${t.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t.team_name}</p>
                    <p className="text-xs text-muted-foreground">{t.member_count || 0} members{t.leader_name ? ` · Leader: ${t.leader_name}` : ''}</p>
                  </div>
                </div>
                <Badge>{t.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


function TimelineTab({ participantId }) {
  const [timeline, setTimeline] = useState([]);
  useEffect(() => {
    api.get(`/participants/${participantId}/timeline?limit=50`).then(r => setTimeline(r.data.data || [])).catch(() => {});
  }, [participantId]);
  return (
    <div className="space-y-2">
      {timeline.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No activity</CardContent></Card>
      : timeline.map((e, i) => {
        const Icon = TIMELINE_ICONS[e.type] || Clock;
        return (
          <div key={i} className="flex gap-3 py-2 border-b last:border-0">
            <div className="mt-1 h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{e.title}</p>
                <StatusBadge status={e.action?.toUpperCase()} className="text-[9px]" />
              </div>
              <p className="text-xs text-muted-foreground">{e.description}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{e.timestamp?.replace('T',' ').slice(0,19)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function CarePlansTab({ participantId }) {
  // Phase 3: delegate to the extracted CarePlanTab component family.
  // Surface is unchanged when v2 flag is OFF; v2 features (templates, ack,
  // diff, linked entities) layer on top when flag is ON.
  // P1 (Structured Matrix Upgrade): pass currentUser for permission-aware
  // gating of the Migrate button / internal-notes editing UI.
  const { user } = useAuth();
  return <CarePlanTabV2 participantId={participantId} currentUser={user} />;
}

function RisksTab({ participantId }) {
  const [risks, setRisks] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', risk_type: 'FALL', likelihood: 'MEDIUM', impact: 'MEDIUM', risk_description: '', control_measures: '' });
  const [editingId, setEditingId] = useState(null);
  const [detailsRow, setDetailsRow] = useState(null);
  const reload = async () => { const r = await api.get(`/risk/by-participant/${participantId}?pageSize=50`); setRisks(r.data.items || r.data.data || []); };
  useEffect(() => { api.get(`/risk/by-participant/${participantId}?pageSize=50`).then(r => setRisks(r.data.items || r.data.data || [])).catch(() => {}); }, [participantId]);

  const create = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/risk/${editingId}`, { title: form.title, risk_type: form.risk_type, likelihood: form.likelihood, impact: form.impact, description: form.risk_description, control_measures: form.control_measures });
      } else {
        await api.post('/risk', { participant_id: participantId, ...form });
      }
      await reload(); setCreateOpen(false); setEditingId(null);
    } catch (e) {}
  };

  const openEditRisk = (r) => {
    setForm({ title: r.title || '', risk_type: r.risk_type || 'FALL', likelihood: r.likelihood || 'MEDIUM', impact: r.impact || 'MEDIUM', risk_description: r.description || r.risk_description || '', control_measures: r.control_measures || r.mitigation || '' });
    setEditingId(r.id); setCreateOpen(true);
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/risk/${id}/${action}`);
      const r = await api.get(`/risk/by-participant/${participantId}?pageSize=50`);
      setRisks(r.data.items || r.data.data || []);
    } catch (e) {}
  };

  const severityColor = (score) => score >= 6 ? 'text-red-600' : score >= 3 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Risk Assessments</h3><Button size="sm" onClick={() => { setEditingId(null); setForm({ title: '', risk_type: 'FALL', likelihood: 'MEDIUM', impact: 'MEDIUM', risk_description: '', control_measures: '' }); setCreateOpen(true); }} data-testid="new-risk-btn"><Plus className="h-3 w-3 mr-1" /> New Risk</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form.risk_type} onValueChange={(v) => setForm({...form, risk_type: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['FALL','MEDICATION','BEHAVIOUR','TRANSPORT','ENVIRONMENT','MISSING_PERSON','OTHER'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Likelihood</Label><Select value={form.likelihood} onValueChange={(v) => setForm({...form, likelihood: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['LOW','MEDIUM','HIGH'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label className="text-xs">Impact</Label><Select value={form.impact} onValueChange={(v) => setForm({...form, impact: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['LOW','MEDIUM','HIGH'].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={form.risk_description} onChange={(e) => setForm({...form, risk_description: e.target.value})} required /></div>
          <div className="space-y-1"><Label className="text-xs">Control Measures</Label><Input value={form.control_measures} onChange={(e) => setForm({...form, control_measures: e.target.value})} className="h-8 text-sm" /></div>
          <div className="flex gap-2"><Button size="sm" type="submit">{editingId ? 'Save Changes' : 'Create'}</Button><Button size="sm" variant="ghost" type="button" onClick={() => { setCreateOpen(false); setEditingId(null); }}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      <Table><TableHeader><TableRow><TableHead>No</TableHead><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Score</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>{risks.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-4 text-muted-foreground">No risks</TableCell></TableRow>
          : risks.map(r => (<TableRow key={r.id}><TableCell className="font-mono text-xs">{r.risk_no}</TableCell><TableCell className="font-medium">{r.title}</TableCell><TableCell><StatusBadge status={r.risk_type} /></TableCell>
            <TableCell><span className={`font-bold ${severityColor(r.risk_score)}`}>{r.risk_score}</span> <span className="text-xs text-muted-foreground">({r.likelihood}/{r.impact})</span></TableCell>
            <TableCell><StatusBadge status={r.status} /></TableCell>
            <TableCell><div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setDetailsRow(r)} data-testid={`risk-details-${r.id}`}>Details</Button>
              {r.status !== 'CLOSED' && <Button size="sm" variant="ghost" onClick={() => openEditRisk(r)} data-testid={`risk-edit-${r.id}`}>Edit</Button>}
              {r.status === 'ACTIVE' && !r.approved_by_user_id && <Button size="sm" variant="ghost" onClick={() => doAction(r.id, 'approve')}>Approve</Button>}
              {r.status !== 'CLOSED' && <Button size="sm" variant="ghost" onClick={() => doAction(r.id, 'close')}>Close</Button>}
            </div></TableCell></TableRow>))}</TableBody></Table>
      <Dialog open={!!detailsRow} onOpenChange={(o) => { if (!o) setDetailsRow(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="risk-details-dialog">
          <DialogHeader>
            <DialogTitle>{detailsRow?.title} <span className="font-mono text-xs text-muted-foreground">{detailsRow?.risk_no}</span></DialogTitle>
            <DialogDescription>Risk assessment details</DialogDescription>
          </DialogHeader>
          {detailsRow && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span>{detailsRow.risk_type}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Likelihood / Impact</span><span>{detailsRow.likelihood} / {detailsRow.impact}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Score</span><span className={`font-bold ${severityColor(detailsRow.risk_score)}`}>{detailsRow.risk_score}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><StatusBadge status={detailsRow.status} /></div>
              <div><p className="text-muted-foreground mb-1">Description</p><p className="whitespace-pre-wrap rounded border p-2 bg-muted/30">{detailsRow.description || detailsRow.risk_description || '—'}</p></div>
              <div><p className="text-muted-foreground mb-1">Control measures</p><p className="whitespace-pre-wrap rounded border p-2 bg-muted/30">{detailsRow.control_measures || detailsRow.mitigation || '—'}</p></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailsRow(null)}>Close</Button>
            {detailsRow?.id && <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/risk/${detailsRow.id}/export-pdf`, '_blank')}>Export PDF</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GoalsTab({ participantId }) {
  const [goals, setGoals] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [progressGoal, setProgressGoal] = useState(null);
  const [outcomesGoal, setOutcomesGoal] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'INDEPENDENCE', target_date: '' });
  const [progressForm, setProgressForm] = useState({ note: '', progress_percent: 0 });
  // Care Plan & Goals v2 — Phase 2: read SMART feature flag from localStorage
  const smartEnabled = (() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      return !!flags.goals_smart_framework_enabled;
    } catch { return false; }
  })();
  const outcomesEnabled = (() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      return !!flags.goal_outcomes_reporting_enabled;
    } catch { return false; }
  })();
  const portalVisibilityEnabled = (() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      return !!flags.portal_goals_visibility_enabled;
    } catch { return false; }
  })();
  useEffect(() => { api.get(`/goals/by-participant/${participantId}?pageSize=50`).then(r => setGoals(r.data.items || r.data.data || [])).catch(() => {}); }, [participantId]);

  const refresh = async () => { const r = await api.get(`/goals/by-participant/${participantId}?pageSize=50`); setGoals(r.data.items || r.data.data || []); };
  const create = async (e) => {
    e.preventDefault();
    try {
      // Phase 2: include SMART/measurement/strategies fields if flag ON; backend
      // discards them when flag OFF — keeps the legacy payload working.
      await api.post('/goals', { participant_id: participantId, ...form });
      refresh();
      setCreateOpen(false);
      setForm({ title: '', description: '', category: 'INDEPENDENCE', target_date: '' });
    } catch (e) {}
  };
  const addProgress = async () => { if (!progressGoal) return; try { await api.post(`/goals/${progressGoal.id}/progress`, progressForm); refresh(); setProgressGoal(null); } catch (e) {} };
  const archive = async (id) => { try { await api.post(`/goals/${id}/archive`); refresh(); } catch (e) {} };

  const lastPercent = (g) => { const ups = g.progress_updates || []; return ups.length > 0 ? ups[ups.length-1].progress_percent || 0 : 0; };

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Goals & Outcomes</h3><Button size="sm" onClick={() => setCreateOpen(true)} data-testid="goals-new-button"><Plus className="h-3 w-3 mr-1" /> New Goal</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={create} className="space-y-3" data-testid="goal-create-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="h-8 text-sm" data-testid="goal-title-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Category</Label><Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['INDEPENDENCE','COMMUNITY_ACCESS','HEALTH','SKILLS','BEHAVIOUR','OTHER'].map(c => <SelectItem key={c} value={c}>{c.replace(/_/g,' ')}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Target Date</Label><TrinitiDatePicker value={form.target_date} onChange={(v) => setForm({...form, target_date: v})} className="h-8 text-sm" testId="goal-target-date" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="h-8 text-sm" /></div>
          <SmartGoalWizard value={form} onChange={setForm} enabled={smartEnabled}
                           participantId={participantId}
                           participantCtx={{ participant_id: participantId }} />
          <div className="flex gap-2"><Button size="sm" type="submit" data-testid="goal-create-submit">Create</Button><Button size="sm" variant="ghost" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      {goals.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No goals</CardContent></Card>
      : goals.map(g => (
        <Card key={g.id} className="hover:border-primary/20 transition-colors" data-testid={`goal-card-${g.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div><p className="font-medium">{g.title}</p><p className="text-xs text-muted-foreground">{g.goal_no} | <StatusBadge status={g.category} /> | Target: {g.target_date || '-'}</p></div>
              <div className="flex items-center gap-2"><StatusBadge status={g.status} />
                {g.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" onClick={() => { setProgressGoal(g); setProgressForm({ note: '', progress_percent: lastPercent(g) }); }} data-testid={`goal-progress-btn-${g.id}`}>+ Progress</Button>}
                {outcomesEnabled && g.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" onClick={() => setOutcomesGoal(g)} data-testid={`goal-outcome-btn-${g.id}`}>+ Outcome</Button>}
                {portalVisibilityEnabled && g.status !== 'ARCHIVED' && (
                  <Button
                    size="sm"
                    variant={g.portal_visible ? 'default' : 'outline'}
                    onClick={async () => {
                      try {
                        await api.patch(`/portal-visibility/goals/${g.id}`, { portal_visible: !g.portal_visible });
                        toast.success(g.portal_visible ? 'Hidden from portal' : 'Published to portal');
                        refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Failed to update portal visibility');
                      }
                    }}
                    data-testid={`goal-portal-toggle-${g.id}`}
                    title={g.portal_visible ? 'Click to hide from portal' : 'Click to publish to portal (participant/family can view)'}
                  >
                    {g.portal_visible ? 'On Portal ✓' : 'Publish to Portal'}
                  </Button>
                )}
                {g.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" onClick={() => archive(g.id)}>Archive</Button>}
              </div>
            </div>
            {g.description && <p className="text-sm text-muted-foreground mb-2">{g.description}</p>}
            <div className="w-full bg-muted rounded-full h-2.5 mb-1"><div className="bg-primary h-2.5 rounded-full transition-all" style={{width: `${lastPercent(g)}%`}} /></div>
            <p className="text-xs text-muted-foreground">{lastPercent(g)}% complete | {(g.progress_updates || []).length} update(s){smartEnabled && g.measurements ? ` | ${g.measurements.length} measurement(s)` : ''}</p>
            {smartEnabled && g.smart && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground">SMART details</summary>
                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-1 pl-2">
                  {['specific','measurable','achievable','relevant','time_bound'].map(k => (
                    g.smart[k] ? <div key={k}><span className="font-medium capitalize">{k.replace('_',' ')}:</span> {g.smart[k]}</div> : null
                  ))}
                </div>
                {g.measurement_type && <div className="mt-1 pl-2"><span className="font-medium">Measurement:</span> {g.measurement_type}</div>}
                {g.support_strategies?.length > 0 && (
                  <div className="mt-1 pl-2"><span className="font-medium">Strategies:</span> {g.support_strategies.join(', ')}</div>
                )}
              </details>
            )}
          </CardContent>
        </Card>
      ))}
      {progressGoal && (
        <Dialog open={!!progressGoal} onOpenChange={() => setProgressGoal(null)}>
          <DialogContent><DialogHeader><DialogTitle>Update Progress: {progressGoal.title}</DialogTitle><DialogDescription>Add a progress note</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Progress (%)</Label><Input type="number" min="0" max="100" value={progressForm.progress_percent} onChange={(e) => setProgressForm({...progressForm, progress_percent: parseInt(e.target.value) || 0})} /></div>
              <div className="space-y-2"><Label>Note</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={progressForm.note} onChange={(e) => setProgressForm({...progressForm, note: e.target.value})} required /></div>
              <Button className="w-full" onClick={addProgress} disabled={!progressForm.note}>Save Progress</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Phase 4.5: Outcomes review dialog */}
      {outcomesEnabled && (
        <OutcomesReviewDialog
          open={!!outcomesGoal}
          onOpenChange={(o) => { if (!o) setOutcomesGoal(null); }}
          goal={outcomesGoal}
          onRecorded={() => { refresh(); setOutcomesGoal(null); }}
        />
      )}
    </div>
  );
}


function MedicationsTab({ participantId }) {
  const [meds, setMeds] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [schedOpen, setSchedOpen] = useState(null);
  const [form, setForm] = useState({ name:'',generic_name:'',form:'TABLET',route:'ORAL',dosage:'',strength:'',prescribed_by:'',instructions:'',prn:false,requires_double_sign:false });
  const [schedForm, setSchedForm] = useState({ schedule_type:'DAILY',times:['08:00'],days_of_week:[],instructions:'' });

  const refresh = async () => {
    const [mR, sR] = await Promise.all([
      api.get(`/medications/by-participant/${participantId}?pageSize=100`),
      api.get(`/medschedule/by-participant/${participantId}?pageSize=100`),
    ]);
    setMeds(mR.data.items || mR.data.data || []);
    setSchedules(sR.data.items || sR.data.data || []);
  };
  useEffect(() => { refresh(); }, [participantId]); // eslint-disable-line

  const createMed = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/medications/${editingId}`, form);
        toast.success('Medication updated');
      } else {
        await api.post('/medications', { participant_id: participantId, ...form });
        toast.success('Medication added');
      }
      refresh();
      setCreateOpen(false);
      setEditingId(null);
    } catch (err) {
      // Keep the dialog open and preserve entered values so the user can retry.
      toast.error(err?.response?.data?.error?.message || err?.response?.data?.detail || 'Failed to save medication');
    }
  };
  const openEditMed = (m) => {
    setForm({
      name: m.name || '', generic_name: m.generic_name || '', form: m.form || 'TABLET',
      route: m.route || 'ORAL', dosage: m.dosage || '', strength: m.strength || '',
      prescribed_by: m.prescribed_by || '', instructions: m.instructions || '',
      prn: !!m.prn, requires_double_sign: !!m.requires_double_sign,
    });
    setEditingId(m.id);
    setCreateOpen(true);
  };
  const stopMed = async (id) => { try { await api.post(`/medications/${id}/stop`); refresh(); } catch (err) { toast.error(err?.response?.data?.error?.message || err?.response?.data?.detail || 'Failed to stop medication'); } };
  const archiveMed = async (id) => { try { await api.post(`/medications/${id}/archive`); refresh(); } catch (err) { toast.error(err?.response?.data?.error?.message || err?.response?.data?.detail || 'Failed to archive medication'); } };
  const addSchedule = async () => {
    if (!schedOpen) return;
    try {
      await api.post('/medschedule', { participant_id: participantId, medication_id: schedOpen, ...schedForm });
      refresh(); setSchedOpen(null);
    } catch(e) {}
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Medications</h3><Button size="sm" onClick={() => { setEditingId(null); setForm({ name:'',generic_name:'',form:'TABLET',route:'ORAL',dosage:'',strength:'',prescribed_by:'',instructions:'',prn:false,requires_double_sign:false }); setCreateOpen(true); }}><Plus className="h-3 w-3 mr-1" /> Add Medication</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={createMed} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Form</Label><Select value={form.form} onValueChange={(v) => setForm({...form, form: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['TABLET','LIQUID','INJECTION','CAPSULE','PATCH','OTHER'].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Route</Label><Select value={form.route} onValueChange={(v) => setForm({...form, route: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['ORAL','TOPICAL','INHALATION','SUBCUTANEOUS','OTHER'].map(r_ => <SelectItem key={r_} value={r_}>{r_}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Dosage</Label><Input value={form.dosage} onChange={(e) => setForm({...form, dosage: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Strength</Label><Input value={form.strength} onChange={(e) => setForm({...form, strength: e.target.value})} className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Prescribed By</Label><Input value={form.prescribed_by} onChange={(e) => setForm({...form, prescribed_by: e.target.value})} className="h-8 text-sm" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Instructions</Label><Input value={form.instructions} onChange={(e) => setForm({...form, instructions: e.target.value})} className="h-8 text-sm" /></div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.prn} onChange={(e) => setForm({...form, prn: e.target.checked})} /> PRN (as needed)</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.requires_double_sign} onChange={(e) => setForm({...form, requires_double_sign: e.target.checked})} /> Requires double sign</label>
          </div>
          <div className="flex gap-2"><Button size="sm" type="submit">{editingId ? 'Save Changes' : 'Create'}</Button><Button size="sm" variant="ghost" type="button" onClick={() => { setCreateOpen(false); setEditingId(null); }}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      <Table><TableHeader><TableRow><TableHead>No</TableHead><TableHead>Name</TableHead><TableHead>Dosage</TableHead><TableHead>Form</TableHead><TableHead>PRN</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>{meds.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-4 text-muted-foreground">No medications</TableCell></TableRow>
          : meds.map(m => (<TableRow key={m.id}><TableCell className="font-mono text-xs">{m.medication_no}</TableCell><TableCell className="font-medium">{m.name}</TableCell>
            <TableCell className="text-sm">{m.dosage}</TableCell><TableCell>{m.form}</TableCell><TableCell>{m.prn ? 'Yes' : '-'}</TableCell><TableCell><StatusBadge status={m.status} /></TableCell>
            <TableCell><div className="flex gap-1">
              {m.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" onClick={() => openEditMed(m)} data-testid={`med-edit-${m.id}`}>Edit</Button>}
              {m.status === 'ACTIVE' && <Button size="sm" variant="ghost" onClick={() => setSchedOpen(m.id)}>Schedule</Button>}
              {m.status === 'ACTIVE' && <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => stopMed(m.id)}>Stop</Button>}
              {m.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => archiveMed(m.id)}>Archive</Button>}
            </div></TableCell></TableRow>))}</TableBody></Table>

      {/* Schedule Dialog */}
      {schedOpen && (
        <Dialog open={!!schedOpen} onOpenChange={() => setSchedOpen(null)}>
          <DialogContent><DialogHeader><DialogTitle>Add Schedule</DialogTitle><DialogDescription>Set medication times</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={schedForm.schedule_type} onValueChange={(v) => setSchedForm({...schedForm, schedule_type: v})}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent>{['DAILY','WEEKLY','SPECIFIC_DAYS','PRN'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              {schedForm.schedule_type !== 'PRN' && <div className="space-y-1"><Label className="text-xs">Times (comma-separated HH:MM)</Label><Input value={schedForm.times.join(',')} onChange={(e) => setSchedForm({...schedForm, times: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} className="h-8 text-sm font-mono" placeholder="08:00,12:00,20:00" /></div>}
              <div className="space-y-1"><Label className="text-xs">Instructions</Label><Input value={schedForm.instructions} onChange={(e) => setSchedForm({...schedForm, instructions: e.target.value})} className="h-8 text-sm" /></div>
              <Button className="w-full" size="sm" onClick={addSchedule}>Add Schedule</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Schedules */}
      {schedules.length > 0 && (<>
        <h4 className="font-medium text-sm mt-4">Schedules</h4>
        <div className="space-y-2">{schedules.map(s => (
          <div key={s.id} className="border rounded-lg p-3 text-sm flex items-center justify-between">
            <div><span className="font-medium">{s.medication_name || 'Medication'}</span> <StatusBadge status={s.schedule_type} className="ml-2" /><p className="text-xs text-muted-foreground mt-0.5">Times: {(s.times || []).join(', ') || 'PRN'} {s.instructions && `| ${s.instructions}`}</p></div>
          </div>
        ))}</div>
      </>)}

      <MedicationAdministrationRecord participantId={participantId} />
    </div>
  );
}



function ParticipantProfileV3OverviewWrapper({ participant, participantId, accessControl, dashboard, canEditOverview }) {
  const [snapshot, setSnapshot] = useState(null);
  const [overviewProfile, setOverviewProfile] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchAll = useCallback(() => {
    api.get(`/participants/${participantId}/support-snapshot`).then(r => setSnapshot(r.data?.data)).catch(() => {});
    api.get(`/participants/${participantId}/overview-profile`).then(r => setOverviewProfile(r.data?.data || {})).catch(() => {});
  }, [participantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Lazy import edit drawer
  const { ParticipantOverviewEditDrawer } = require('@/components/participants/profile/overview/ParticipantOverviewEditDrawer');

  return (
    <>
      <ParticipantProfileV3Layout
        participant={participant}
        participantId={participantId}
        accessControl={accessControl}
        dashboard={dashboard}
        snapshot={snapshot}
        overviewProfile={overviewProfile}
        canEditOverview={canEditOverview}
        onEditOverview={() => setEditOpen(true)}
        onEditSnapshot={() => setEditOpen(true)}
        onRefreshSnapshot={fetchAll}
      />
      {editOpen && (
        <ParticipantOverviewEditDrawer
          participantId={participantId}
          currentProfile={overviewProfile || {}}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchAll(); }}
        />
      )}
    </>
  );
}



export default function ParticipantDetailPage() {
  const { dangerConfirm } = useAppDialog();
  const { participantId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canViewCharts = (user?.permissions || []).includes('participant_charts.view');
  const canViewParticipantDocs = (user?.permissions || []).includes('participants.view_documents');
  const canViewSnapshot = (user?.permissions || []).includes('participants.view_support_snapshot');
  const canOpenReadinessCentre = (user?.permissions || []).includes('participants.view');  // same permission the backend readiness-centre route enforces
  const canScheduleHome = ['services.manage', 'roster.manage'].some((p) => (user?.permissions || []).includes(p));   // RP-3 Option B (E2 / E1 guards)
  const canManageParticipant = (user?.permissions || []).includes('participants.manage');  // same permission PUT /participants/{id} enforces
  const readinessCentreButton = canOpenReadinessCentre ? (
    <Button variant="outline" size="sm" onClick={() => navigate(`/participants/${participantId}/readiness-centre`)} data-testid="readiness-centre-btn">
      <Shield className="h-4 w-4 mr-1" /> Readiness Centre
    </Button>
  ) : null;
  const [participant, setParticipant] = useState(null);
  const [editParticipantOpen, setEditParticipantOpen] = useState(false); // shared canonical Edit Participant dialog (PUT /participants/{id})
  const [fundingPlans, setFundingPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [agreementsForLines, setAgreementsForLines] = useState([]);   // RP-3 R2: line-aware "Add Service Item"
  const [scheduleFocusAgreementId, setScheduleFocusAgreementId] = useState(null);   // RP-3 Option A
  
  // RBAC Access Control
  const [accessControl, setAccessControl] = useState({
    is_full_access: true,
    can_edit: true,
    can_view_funding: true,
    can_view_ndis: true,
    can_view_documents: true,
    can_view_incidents: true,
    can_export_pdf: true,
    show_funding_amount: true,
    show_ndis_number: true,
    blocked_tabs: [],
  });

  // Dialogs
  const [fundingDialog, setFundingDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [riskAlerts, setRiskAlerts] = useState(null);
  const [chartsFlagEnabled, setChartsFlagEnabled] = useState(false);
  const [participantDocsFlagEnabled, setParticipantDocsFlagEnabled] = useState(false);
  const [snapshotFlagEnabled, setSnapshotFlagEnabled] = useState(false);
  const [snapshotEditFlagEnabled, setSnapshotEditFlagEnabled] = useState(false);
  const [snapshotIntelFlagEnabled, setSnapshotIntelFlagEnabled] = useState(false);
  const [headerV2Enabled, setHeaderV2Enabled] = useState(false);
  const [overviewV2Enabled, setOverviewV2Enabled] = useState(false);
  const [overviewEditEnabled, setOverviewEditEnabled] = useState(false);
  const [profileV3Enabled, setProfileV3Enabled] = useState(false);
  const [timelineV2Enabled, setTimelineV2Enabled] = useState(false);
  const [overviewConsolidationEnabled, setOverviewConsolidationEnabled] = useState(false);
  const [caresupportLandingEnabled, setCaresupportLandingEnabled] = useState(false);
  const [scheduleLandingEnabled, setScheduleLandingEnabled] = useState(false);
  const [notesdocsLandingEnabled, setNotesdocsLandingEnabled] = useState(false);
  const [careplanDomainsEnabled, setCareplanDomainsEnabled] = useState(false);
  const [structuredMatrixEnabled, setStructuredMatrixEnabled] = useState(false);

  // Fetch feature flags to decide tab visibility (hidden if OFF)
  useEffect(() => {
    let cancelled = false;
    api.get('/production/feature-flags/mine')
      .then(r => {
        if (cancelled) return;
        const flags = r?.data?.data || {};
        setChartsFlagEnabled(!!flags.charts_module_enabled);
        setParticipantDocsFlagEnabled(!!flags.participant_documents_enabled);
        setSnapshotFlagEnabled(!!flags.participant_support_snapshot_enabled);
        setSnapshotEditFlagEnabled(!!flags.participant_support_snapshot_edit_enabled);
        setSnapshotIntelFlagEnabled(!!flags.participant_support_snapshot_intelligence_enabled);
        setHeaderV2Enabled(!!flags.participant_profile_header_v2_enabled);
        setOverviewV2Enabled(!!flags.participant_overview_v2_enabled);
        setOverviewEditEnabled(!!flags.participant_overview_edit_enabled);
        setProfileV3Enabled(!!flags.participant_profile_ui_v3_enabled);
        setTimelineV2Enabled(!!flags.participant_timeline_enabled);
        setOverviewConsolidationEnabled(!!flags.participant_overview_consolidation_enabled);
        setCaresupportLandingEnabled(!!flags.participant_caresupport_landing_enabled);
        setScheduleLandingEnabled(!!flags.participant_schedule_landing_enabled);
        setNotesdocsLandingEnabled(!!flags.participant_notesdocs_landing_enabled);
        setCareplanDomainsEnabled(!!flags.participant_careplan_domains_enabled);
        setStructuredMatrixEnabled(!!flags.care_plan_v2_structured_matrix_enabled);
      })
      .catch(() => {
        if (!cancelled) {
          setChartsFlagEnabled(false);
          setParticipantDocsFlagEnabled(false);
          setSnapshotFlagEnabled(false);
          setSnapshotEditFlagEnabled(false);
          setSnapshotIntelFlagEnabled(false);
          setHeaderV2Enabled(false);
          setOverviewV2Enabled(false);
          setOverviewEditEnabled(false);
          setProfileV3Enabled(false);
          setTimelineV2Enabled(false);
          setOverviewConsolidationEnabled(false);
          setCaresupportLandingEnabled(false);
          setScheduleLandingEnabled(false);
          setNotesdocsLandingEnabled(false);
          setCareplanDomainsEnabled(false);
          setStructuredMatrixEnabled(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const showChartsTab = chartsFlagEnabled && canViewCharts;
  const showDocumentsTab = participantDocsFlagEnabled && canViewParticipantDocs;
  const showSnapshot = snapshotFlagEnabled && canViewSnapshot;

  // Phase 2F (Feb 2026): gate the Onboarding sub-tab on the same flag the
  // OnboardingChecklistPanel honours. Reading from the same localStorage cache
  // that `AuthContext.syncFeatureFlags` populates keeps this consistent without
  // a separate fetch.
  const onboardingChecklistFlagEnabled = (() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      return !!flags.participant_onboarding_checklist_enabled;
    } catch {
      return false;
    }
  })();

  // Phase 4.5: gate the new "Outcomes" sub-tab on the same flag the
  // OutcomesReviewDialog / OutcomeSummaryCard already honour. Default OFF.
  const outcomesReportingFlagEnabled = (() => {
    try {
      const flags = JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}');
      return !!flags.goal_outcomes_reporting_enabled;
    } catch {
      return false;
    }
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // PHASE 1 IA REFACTOR: 27 flat tabs → 10 grouped parent tabs (frontend-only)
  // - Horizontal sub-tab strip under the parent strip
  // - URL state: ?tab=<parent>&sub=<leaf> (backwards-compat with legacy ?tab=<leaf>)
  // - All existing data-testids preserved (tab-<leaf>); parent strip adds parent-tab-<parent>
  // - Per-sub-tab role gating preserved via `visible` flags
  // ─────────────────────────────────────────────────────────────────────────
  const participantTabGroups = useMemo(() => ([
    {
      id: 'overview', label: 'Overview',
      children: [
        { id: 'overview', label: 'Overview', visible: true },
        { id: 'onboarding', label: 'Onboarding', visible: !!onboardingChecklistFlagEnabled },
      ],
    },
    {
      id: 'care-support', label: 'Care & Support',
      children: [
        { id: 'landing', label: 'Overview', visible: !!caresupportLandingEnabled },
        { id: 'careplans', label: 'Care Plans', visible: true },
        { id: 'goals', label: 'Goals', visible: true },
        { id: 'outcomes', label: 'Outcomes', visible: !!outcomesReportingFlagEnabled },
        { id: 'agreements', label: 'Agreements', visible: !!accessControl.is_full_access || !!(user?.permissions || []).includes('agreements.view') },
        { id: 'support-team', label: 'Support Team', visible: true },
        { id: 'teams', label: 'Teams', visible: !!accessControl.is_full_access },
        { id: 'continuity', label: 'Continuity', visible: true },
      ],
    },
    {
      id: 'clinical', label: 'Clinical',
      children: [
        { id: 'medications', label: 'Medications', visible: true },
        { id: 'charts', label: 'Vital Charts', visible: !!showChartsTab },
        { id: 'allergies', label: 'Allergies', visible: true },
        { id: 'health-conditions', label: 'Health Conditions', visible: true },
      ],
    },
    {
      id: 'notes-docs', label: 'Notes & Docs',
      children: [
        { id: 'notesdocs-landing', label: 'Overview', visible: !!notesdocsLandingEnabled, group: 'notes-docs' },
        { id: 'notes', label: !!notesdocsLandingEnabled ? 'Progress Notes' : 'Notes', visible: true },
        { id: 'handover', label: 'Handover Notes', visible: true },
        { id: 'assessments', label: 'Assessments', visible: !!notesdocsLandingEnabled, group: 'notes-docs' },
        { id: 'reports', label: 'Reports', visible: !!notesdocsLandingEnabled, group: 'notes-docs' },
        { id: 'consents', label: !!notesdocsLandingEnabled ? 'Consent Forms' : 'Consents', visible: !!accessControl.is_full_access },
        { id: 'tasks', label: 'Tasks', visible: true },
        { id: 'documents', label: !!notesdocsLandingEnabled ? 'Upload Documents & Forms' : 'Documents', visible: !!showDocumentsTab },
        { id: 'document-categories', label: 'Document Categories', visible: !!notesdocsLandingEnabled, group: 'notes-docs' },
        { id: 'forms', label: 'Forms', visible: !!accessControl.is_full_access && !notesdocsLandingEnabled },
        { id: 'expiring', label: !!notesdocsLandingEnabled ? 'Review & Expiry' : 'Expiring Items', visible: !!accessControl.is_full_access },
      ],
    },
    {
      id: 'schedule', label: 'Schedule',
      children: [
        // RP-3 Option B: the Schedules home is flag-independent (services.manage ∨ roster.manage), first in the group
        { id: 'schedules', label: 'Schedules', visible: !!canScheduleHome, group: 'schedule' },
        { id: 'schedule-landing', label: 'Overview', visible: !!scheduleLandingEnabled, group: 'schedule' },
        { id: 'rosters', label: 'Rosters', visible: !!scheduleLandingEnabled, group: 'schedule' },
        { id: 'appointments', label: 'Appointments', visible: true },
        { id: 'group-sessions', label: !!scheduleLandingEnabled ? 'Activities' : 'Group Sessions', visible: true },
        { id: 'transport', label: 'Transport Bookings', visible: !!scheduleLandingEnabled, group: 'schedule' },
        { id: 'calendar', label: 'Calendar', visible: !!scheduleLandingEnabled, group: 'schedule' },
        { id: 'appointment-outcomes', label: 'Appointment Outcomes', visible: !!scheduleLandingEnabled, group: 'schedule' },
      ],
    },
    {
      id: 'accommodation', label: 'Accommodation',
      children: [
        { id: 'addresses', label: 'Addresses', visible: true },
        { id: 'accommodation-current', label: 'Current Residence', visible: true },
        { id: 'accommodation-history', label: 'Tenancy History', visible: true },
        { id: 'accommodation-overlay', label: 'Overlay', visible: !!accessControl.is_full_access },
      ],
    },
    {
      id: 'funding', label: 'Funding',
      children: [
        { id: 'funding', label: 'Plans & Ledger', visible: !!accessControl.can_view_funding },
        { id: 'ndis-details', label: 'NDIS Details', visible: !!accessControl.can_view_ndis },
        { id: 'services', label: 'Recurring Services', visible: !!accessControl.can_view_funding },
      ],
    },
    {
      id: 'communication', label: 'Communication',
      children: [
        { id: 'contacts', label: 'Contacts', visible: true },
        { id: 'email-log', label: 'Email Log', visible: true },
        { id: 'sms-log', label: 'SMS Log', visible: true },
        { id: 'engagement-threads', label: 'Engagement Threads', visible: true },
      ],
    },
    {
      id: 'risk', label: 'Risk',
      children: [
        { id: 'risk-profile', label: 'Risk Profile', visible: true },
        { id: 'risks', label: 'Risk Register', visible: !!accessControl.is_full_access },
        { id: 'flags', label: 'Flags', visible: !!accessControl.is_full_access },
        { id: 'preferences', label: 'Preferences', visible: true },
      ],
    },
    {
      id: 'administration', label: 'Administration',
      children: [
        { id: 'tags', label: 'Tags', visible: !!accessControl.is_full_access },
        { id: 'duplicates', label: 'Duplicates', visible: !!accessControl.is_full_access },
        { id: 'linked-modules', label: 'Linked Modules', visible: !!accessControl.is_full_access },
        { id: 'timeline', label: 'Audit Log', visible: !!accessControl.is_full_access },
        { id: 'portal-access', label: 'Portal Access', visible: (user?.permissions || []).includes('portal.manage_access') },
      ],
    },
  ]), [accessControl, showChartsTab, showDocumentsTab, onboardingChecklistFlagEnabled, outcomesReportingFlagEnabled, caresupportLandingEnabled, scheduleLandingEnabled, notesdocsLandingEnabled, canScheduleHome, user]);

  const leafToParent = useMemo(() => {
    const map = {};
    participantTabGroups.forEach(g => g.children.forEach(c => { map[c.id] = g.id; }));
    return map;
  }, [participantTabGroups]);

  // Resolve initial leaf from URL: support legacy ?tab=<leaf> AND new ?tab=<parent>&sub=<leaf>.
  //
  // Invalid-submenu fallback (approved 23 Jul 2026): when ?sub= holds an unknown
  // value AND ?tab= names a parent group that owns a landing overview
  // (`schedule-landing`, `caresupport-landing`, `notesdocs-landing`), redirect to
  // that landing rather than leaving the sub-tab body blank. Otherwise fall back
  // to the parent group's first visible child, or ultimately to the Overview tab.
  const _legacyTab = searchParams.get('tab');
  const _subParam = searchParams.get('sub');
  const _resolveInitialLeaf = () => {
    if (_subParam && leafToParent[_subParam]) return _subParam;
    if (_legacyTab && leafToParent[_legacyTab]) return _legacyTab;
    // Unknown ?sub= but known parent tab: prefer that parent's landing if flag is
    // ON, else its first visible child.
    if (_legacyTab) {
      const g = participantTabGroups.find((grp) => grp.id === _legacyTab);
      if (g) {
        // Landing leaf id convention: parent id with dashes stripped + '-landing'
        // (e.g. `notes-docs` -> `notesdocs-landing`; `care-support` -> `caresupport-landing`;
        //  `schedule` -> `schedule-landing`).
        const landingId = `${_legacyTab.replace(/-/g, '')}-landing`;
        const landing = g.children.find((c) => c.id === landingId && c.visible);
        if (landing) return landing.id;
        const fv = g.children.find((c) => c.visible);
        if (fv) return fv.id;
      }
    }
    return 'overview';
  };
  // Track whether the initial URL contained an invalid ?sub= value so a later
  // effect can normalise it to the landing once feature-flag driven children
  // become visible. Approved 23 Jul 2026 correction.
  const _hadInvalidSubRef = useRef(!!(_subParam && !leafToParent[_subParam]));

  const [activeLeaf, setActiveLeaf] = useState(_resolveInitialLeaf);
  const activeParent = leafToParent[activeLeaf] || 'overview';

  // Backwards-compat redirect: if URL came in with ?tab=<leaf> (legacy), upgrade it to ?tab=<parent>&sub=<leaf>.
  useEffect(() => {
    if (_legacyTab && leafToParent[_legacyTab] && !_subParam) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', leafToParent[_legacyTab]);
      next.set('sub', _legacyTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invalid-submenu fallback (approved 23 Jul 2026): when the ORIGINAL URL held an
  // unknown ?sub= value, wait for feature-flag driven children to become visible
  // and then normalise the URL to the parent's landing (if visible) or its first
  // visible child. Uses a ref because the initial resolution may have already
  // promoted the sub to a valid non-landing leaf before flags loaded.
  useEffect(() => {
    if (!_hadInvalidSubRef.current) return;
    const curTab = searchParams.get('tab');
    if (!curTab) return;
    const g = participantTabGroups.find((grp) => grp.id === curTab);
    if (!g) return;
    const landingId = `${curTab.replace(/-/g, '')}-landing`;
    const target = g.children.find((c) => c.id === landingId && c.visible)
      || g.children.find((c) => c.visible);
    if (!target) return;
    // Only rewrite once — clear the flag so subsequent explicit user navigation
    // is not overridden.
    _hadInvalidSubRef.current = false;
    const next = new URLSearchParams(searchParams);
    next.set('tab', curTab);
    next.set('sub', target.id);
    setSearchParams(next, { replace: true });
    setActiveLeaf(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesdocsLandingEnabled, scheduleLandingEnabled, caresupportLandingEnabled, leafToParent]);

  const handleLeafChange = useCallback((nextLeaf) => {
    setActiveLeaf(nextLeaf);
    const parent = leafToParent[nextLeaf] || 'overview';
    const next = new URLSearchParams(window.location.search);
    next.set('tab', parent);
    next.set('sub', nextLeaf);
    setSearchParams(next, { replace: true });
  }, [leafToParent, setSearchParams]);

  const handleParentChange = useCallback((nextParentId) => {
    const group = participantTabGroups.find(g => g.id === nextParentId);
    if (!group) return;
    const firstVisible = group.children.find(c => c.visible) || group.children[0];
    handleLeafChange(firstVisible.id);
  }, [participantTabGroups, handleLeafChange]);

  // Phase 1 Overview Consolidation: resolve a logical dashboard target to the
  // most relevant VISIBLE/PERMITTED leaf tab. Returns null when nothing suitable
  // is available so the calling action can be hidden/disabled safely.
  const availableLeaves = useMemo(() => {
    const s = new Set();
    participantTabGroups.forEach(g => g.children.forEach(c => { if (c.visible) s.add(c.id); }));
    return s;
  }, [participantTabGroups]);

  const resolveOverviewTab = useCallback((target) => {
    // 'overview' target is the dashboard itself (already the active tab) → no-op, hide it.
    if (target === 'overview') return null;
    const CANDIDATES = {
      funding: ['funding', 'ndis-details', 'services'],
      ndis: ['ndis-details', 'funding'],
      communication: ['contacts', 'email-log'],
      contacts: ['contacts', 'email-log'],
      schedule: ['schedules', 'appointments', 'group-sessions'],
      risks: ['risks', 'flags', 'risk-profile'],
      preferences: ['preferences'],
      'support-team': ['support-team'],
      timeline: ['timeline'],
      clinical: ['medications', 'health-conditions', 'allergies'],   // NAV-1: Clinical Overview → Clinical group
      tasks: ['tasks'],
      goals: ['goals', 'outcomes'],
    };
    const cands = CANDIDATES[target] || [target];
    return cands.find(c => availableLeaves.has(c)) || null;
  }, [availableLeaves]);

  const goToOverviewTab = useCallback((target) => {
    const leaf = resolveOverviewTab(target);
    if (leaf) handleLeafChange(leaf);
  }, [resolveOverviewTab, handleLeafChange]);

  const currentGroup = participantTabGroups.find(g => g.id === activeParent) || participantTabGroups[0];
  const visibleParents = participantTabGroups.filter(g => g.children.some(c => c.visible));


  // Forms
  const [fpForm, setFpForm] = useState({ plan_name:'', start_date:'', end_date:'', total_budget:'', core:'', capacity:'', capital:'' });
  const [siForm, setSiForm] = useState({ service_catalog_id:'', funding_plan_id:'', branch_id:'', support_ratio:'1:1', day_of_week:'0', start_time:'09:00', end_time:'13:00',
    uiFreq:'Weekly', days_of_week:[0], monthly_mode:'DAY_OF_MONTH', day_of_month:'1', nth_n:'1', nth_weekday:'0',
    recurrence_start_date:'', recurrence_end_date:'', one_off_date:'' });
  const [genForm, setGenForm] = useState({ mode:'PRESET', preset:'NEXT_4_WEEKS', from_date:'', to_date:'', count:'4', support_ratio:'1:1' });
  const [genOpenFor, setGenOpenFor] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      // Essential data - participant info (all roles can access)
      const pRes = await api.get(`/participants/${participantId}`);
      setParticipant(pRes.data.data);
      
      // Store access control metadata from API response
      if (pRes.data.access) {
        setAccessControl(pRes.data.access);
      }
      
      // Admin-only data - gracefully handle permission errors
      const [fpRes, txRes, siRes, svcRes, stRes, brRes, agRes] = await Promise.allSettled([
        api.get(`/funding/plans/by-participant/${participantId}`),
        api.get(`/funding/transactions/by-participant/${participantId}`),
        api.get('/services/participant-items'),
        api.get('/services/catalog?mode=selection'),
        api.get('/org/states'),
        api.get('/org/branches'),
        api.get(`/agreements/by-participant/${participantId}`),
      ]);
      
      if (fpRes.status === 'fulfilled') setFundingPlans(fpRes.value.data.data || []);
      if (agRes.status === 'fulfilled') setAgreementsForLines(agRes.value.data.data || []);
      if (txRes.status === 'fulfilled') setTransactions(txRes.value.data.data || []);
      if (siRes.status === 'fulfilled') setServiceItems((siRes.value.data.data || []).filter(i => i.participant_id === participantId));
      if (svcRes.status === 'fulfilled') setServices(svcRes.value.data.data || []);
      if (stRes.status === 'fulfilled') setStates(stRes.value.data.data || []);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.data || []);

      // Load dashboard + risk alerts (non-blocking)
      const [dashR, riskR, ovsR] = await Promise.allSettled([
        api.get(`/participants/${participantId}/dashboard`),
        api.get(`/participants/${participantId}/risk-alerts`),
        api.get(`/participants/${participantId}/overview-summary`),
      ]);
      if (dashR.status === 'fulfilled') setDashboard(dashR.value.data.data);
      if (riskR.status === 'fulfilled') setRiskAlerts(riskR.value.data.data);
      // Care Plan & Goals v2 — merge `care_and_goals` block from overview-summary
      // into the existing `dashboard` state so the chart card has one source.
      if (ovsR.status === 'fulfilled' && ovsR.value.data.data?.care_and_goals) {
        setDashboard(prev => ({ ...(prev || {}), care_and_goals: ovsR.value.data.data.care_and_goals }));
      }
    } catch (e) { notify.error(e, 'PARTICIPANT_LOAD_FAILED'); }
  }, [participantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createFundingPlan = async (e) => {
    e.preventDefault();
    try {
      const cats = [];
      if (fpForm.core) cats.push({ type:'CORE', allocated: parseFloat(fpForm.core) });
      if (fpForm.capacity) cats.push({ type:'CAPACITY', allocated: parseFloat(fpForm.capacity) });
      if (fpForm.capital) cats.push({ type:'CAPITAL', allocated: parseFloat(fpForm.capital) });
      const total = parseFloat(fpForm.total_budget) || cats.reduce((s,c) => s + c.allocated, 0);
      await api.post('/funding/plans', {
        participant_id: participantId, plan_name: fpForm.plan_name,
        start_date: fpForm.start_date, end_date: fpForm.end_date,
        total_budget: total, categories: cats,
        state_id: participant?.state_id, branch_id: participant?.branch_id,
      });
      toast.success('Funding plan created'); setFundingDialog(false); fetchData();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const createServiceItem = async (e) => {
    e.preventDefault();
    if (!siForm.branch_id) { toast.error('Please select a service branch'); return; }
    const line = schedulableLines(agreementsForLines).find((l) => l.key === siForm.line_key);   // RP-3 R2: line-aware
    if (!line) { toast.error('Select a line of an ACTIVE service agreement (§8)'); return; }
    const err = validateRuleForm(siForm);
    if (err) { toast.error(err); return; }
    try {
      await api.post('/services/participant-items', {
        participant_id: participantId,
        service_catalog_id: line.service_catalog_id,
        agreement_id: line.agreement_id,
        agreement_line_id: line.agreement_line_id,
        funding_plan_id: siForm.funding_plan_id,
        branch_id: siForm.branch_id,
        support_ratio: siForm.support_ratio,
        ...buildRulePayload(siForm),
        state_id: participant?.state_id,
      });
      toast.success('Service item created'); setItemDialog(false); fetchData();
    } catch (e) { notify.error(e, 'SERVICE_ITEM_CREATE_FAILED'); }
  };

  const generateShifts = async (id, body) => {
    setGenerating(id);
    await runGenerate({ api, toast, navigate, notify }, id, body, () => { setGenOpenFor(null); fetchData(); });   // RP-3: ONE generate helper
    setGenerating(null);
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/participants/${participantId}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participant_${participant.participant_code || participantId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      notify.error(null, 'EXPORT_FAILED');
    }
  };

  if (!participant) return <div className="py-12 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" size="sm" onClick={() => navigate('/participants/list')} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to participants
      </Button>

      <ParticipantEditDialog open={editParticipantOpen} onOpenChange={setEditParticipantOpen} participant={participant} onSaved={fetchData} />

      {/* Header */}
      {headerV2Enabled ? (
        <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="participant-header-wrapper">
          <div className="flex-1 min-w-0">
            <ParticipantHeaderV2 participant={participant} showNdis={accessControl.show_ndis_number} canEdit={accessControl.can_edit} onPhotoUpdated={fetchData} />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {canManageParticipant && (
              <Button variant="outline" size="sm" onClick={() => setEditParticipantOpen(true)} data-testid="edit-participant-btn">
                <FileEdit className="h-4 w-4 mr-1" /> Edit Participant
              </Button>
            )}
            {readinessCentreButton}
            {accessControl.can_export_pdf && (
              <Button variant="outline" size="sm" onClick={handleExportPDF} title="Participant record summary (profile, plan, status lines) — NOT the service agreement document; agreement PDFs open from Agreements ▸ View document" data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export participant summary
              </Button>
            )}
            <StatusBadge status={participant.participant_status || participant.status || 'active'} />
            {accessControl.can_edit && (
              <Select value={participant.participant_status || 'ACTIVE'} onValueChange={async v => {
                const isExit = v === 'EXITED';
                const result = await dangerConfirm({
                  title: isExit ? 'Exit participant?' : `Change status to ${v}?`,
                  description: isExit
                    ? 'The participant will be marked as exited. Provide an exit reason for audit.'
                    : 'Provide a reason so the audit trail captures why the status changed.',
                  confirmLabel: isExit ? 'Exit participant' : 'Change status',
                  reasonRequired: true,
                  variant: isExit ? 'danger' : 'warning',
                  testId: 'participant-status-change-confirm',
                });
                if (!result) return;
                try { await api.patch(`/participants/${participantId}/status`, { new_status: v, reason: result.reason || '', exit_reason: isExit ? 'OTHER' : undefined }); toast.success(`Status changed to ${v}`); fetchData(); }
                catch (e) { notify.error(e, 'PARTICIPANT_UPDATE_FAILED'); }
              }}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="status-change"><SelectValue /></SelectTrigger>
                <SelectContent>{['ENQUIRY','ONBOARDING','ACTIVE','ON_HOLD','INACTIVE','EXITED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {participant.preferred_name ? `${participant.preferred_name} (${participant.first_name} ${participant.last_name})` : `${participant.first_name} ${participant.last_name}`}
            </h1>
            {accessControl.show_ndis_number && participant.ndis_number && (
              <p className="text-muted-foreground font-mono text-sm mt-1">NDIS: {participant.ndis_number}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canManageParticipant && (
              <Button variant="outline" size="sm" onClick={() => setEditParticipantOpen(true)} data-testid="edit-participant-btn">
                <FileEdit className="h-4 w-4 mr-1" /> Edit Participant
              </Button>
            )}
            {readinessCentreButton}
            {accessControl.can_export_pdf && (
              <Button variant="outline" size="sm" onClick={handleExportPDF} title="Participant record summary (profile, plan, status lines) — NOT the service agreement document; agreement PDFs open from Agreements ▸ View document" data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export participant summary
              </Button>
            )}
            <StatusBadge status={participant.participant_status || participant.status || 'active'} />
            {accessControl.can_edit && (
              <Select value={participant.participant_status || 'ACTIVE'} onValueChange={async v => {
                const isExit = v === 'EXITED';
                const result = await dangerConfirm({
                  title: isExit ? 'Exit participant?' : `Change status to ${v}?`,
                  description: isExit
                    ? 'The participant will be marked as exited. Provide an exit reason for audit.'
                    : 'Provide a reason so the audit trail captures why the status changed.',
                  confirmLabel: isExit ? 'Exit participant' : 'Change status',
                  reasonRequired: true,
                  variant: isExit ? 'danger' : 'warning',
                  testId: 'participant-status-change-confirm-2',
                });
                if (!result) return;
                try { await api.patch(`/participants/${participantId}/status`, { new_status: v, reason: result.reason || '', exit_reason: isExit ? 'OTHER' : undefined }); toast.success(`Status changed to ${v}`); fetchData(); }
                catch (e) { notify.error(e, 'PARTICIPANT_UPDATE_FAILED'); }
              }}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="status-change"><SelectValue /></SelectTrigger>
                <SelectContent>{['ENQUIRY','ONBOARDING','ACTIVE','ON_HOLD','INACTIVE','EXITED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {/* Risk Alert Banner */}
      {riskAlerts?.has_alerts && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg" data-testid="risk-alert-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">{riskAlerts.count} Active Risk Alert{riskAlerts.count > 1 ? 's' : ''}</p>
            <div className="flex gap-2 mt-1 flex-wrap">
              {riskAlerts.alerts.map(a => (
                <Badge key={a.id} variant="destructive" className="text-xs">{a.severity}: {a.risk_type?.replace(/_/g, ' ')} — {a.description?.slice(0, 40)}</Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Support Snapshot — hidden when V3 is on (V3 has its own integrated snapshot) */}
      {showSnapshot && !profileV3Enabled && (
        <SupportSnapshotCard participantId={participantId} editFlagEnabled={snapshotEditFlagEnabled} intelligenceEnabled={snapshotIntelFlagEnabled} />
      )}

      {/* Phase 2E PR-2 — Participant Readiness banner (feature-flag gated, default OFF).
          Informational only; never blocks shifts or service delivery in v1. */}
      {canOpenReadinessCentre && <ReadinessCentreSummary participantId={participantId} />}


      {/* Tabs — Phase 1 IA: 10 grouped parent tabs with horizontal sub-tab strip */}
      <Tabs value={activeLeaf} onValueChange={handleLeafChange} data-testid="participant-tabs">
        {/* Parent strip (10 grouped sections) */}
        <div
          className="flex flex-wrap gap-1 border-b pb-1 mb-2"
          role="tablist"
          aria-label="Participant sections"
          data-testid="participant-parent-tabs"
        >
          {visibleParents.map(group => {
            const isActive = group.id === activeParent;
            return (
              <button
                key={group.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`parent-tab-${group.id}`}
                onClick={() => handleParentChange(group.id)}
                className={
                  'px-3 py-1.5 text-sm rounded-md transition-colors ' +
                  (isActive
                    ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                }
              >
                {group.label}
              </button>
            );
          })}
        </div>

        {/* Sub-tab strip (children of currently active parent) — only render when >1 visible child */}
        {currentGroup.children.filter(c => c.visible).length > 1 && (
          <TabsList
            className="flex flex-wrap h-auto gap-1"
            data-testid={`participant-subtabs-${currentGroup.id}`}
          >
            {currentGroup.children.filter(c => c.visible).map(child => (
              <TabsTrigger
                key={child.id}
                value={child.id}
                data-testid={`tab-${child.id}`}
              >
                {child.label}
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        {/* Single-child parent: still expose the leaf testid for tests/deep-links (visually hidden) */}
        {currentGroup.children.filter(c => c.visible).length === 1 && (
          <TabsList className="sr-only" aria-hidden="true">
            {currentGroup.children.filter(c => c.visible).map(child => (
              <TabsTrigger
                key={child.id}
                value={child.id}
                data-testid={`tab-${child.id}`}
                tabIndex={-1}
              >
                {child.label}
              </TabsTrigger>
            ))}
          </TabsList>
        )}


        {/* V3: Inline Timeline Preview below Overview */}
        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {overviewConsolidationEnabled ? (
            <ParticipantOverviewDashboard
              participantId={participantId}
              goToTab={goToOverviewTab}
              resolveTab={resolveOverviewTab}
            />
          ) : profileV3Enabled ? (
            <ParticipantProfileV3OverviewWrapper
              participant={participant}
              participantId={participantId}
              accessControl={accessControl}
              dashboard={dashboard}
              canEditOverview={overviewEditEnabled && (user?.permissions || []).includes('participants.manage')}
            />
          ) : overviewV2Enabled ? (
            <ParticipantOverviewV2
              participant={participant}
              participantId={participantId}
              accessControl={accessControl}
              dashboard={profileV3Enabled ? null : dashboard}
              editEnabled={overviewEditEnabled}
              onRefresh={fetchData}
            />
          ) : (
            <>
          {/* Dashboard KPI Cards */}
          {dashboard && (
            <div className={`grid grid-cols-2 ${accessControl.show_funding_amount ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`} data-testid="participant-dashboard">
              {accessControl.show_funding_amount && (
                <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold text-green-600">${dashboard.funding_remaining?.toLocaleString()}</p><p className="text-[10px] text-muted-foreground" title={`Tracked internally by ${getBrandName()} from approved services. May not equal the participant's live NDIS plan balance, especially if they use more than one provider.`} data-testid="tracked-remaining-label">{getBrandName()} Tracked Remaining</p></CardContent></Card>
              )}
              <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.active_shifts}</p><p className="text-[10px] text-muted-foreground">Active Shifts</p></CardContent></Card>
              <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.assigned_staff_count}</p><p className="text-[10px] text-muted-foreground">Assigned Staff</p></CardContent></Card>
              <Card className={dashboard.high_critical_risks > 0 ? 'border-red-200' : ''}><CardContent className="py-3 text-center"><p className={`text-xl font-bold ${dashboard.high_critical_risks > 0 ? 'text-red-600' : ''}`}>{dashboard.active_risks}</p><p className="text-[10px] text-muted-foreground">Active Risks</p></CardContent></Card>
              <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.upcoming_appointments}</p><p className="text-[10px] text-muted-foreground">Upcoming Appts</p></CardContent></Card>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {/* FRD Section 6.2 - Personal Details */}
                  <div><span className="text-muted-foreground">Full Name:</span><br/><span className="font-medium">{[participant.first_name, participant.middle_name, participant.last_name].filter(Boolean).join(' ') || '-'}</span></div>
                  <div><span className="text-muted-foreground">Preferred Name:</span><br/><span className="font-medium">{participant.preferred_name || '-'}</span></div>
                  <div><span className="text-muted-foreground">Date of Birth:</span><br/><span className="font-medium">{participant.date_of_birth || '-'}</span></div>
                  <div><span className="text-muted-foreground">Gender:</span><br/><span className="font-medium">{participant.gender || '-'}{participant.pronouns && <span className="text-muted-foreground ml-1">({participant.pronouns})</span>}</span></div>
                  <div><span className="text-muted-foreground">Phone:</span><br/><span className="font-medium">{participant.phone || '-'}</span></div>
                  {participant.alternate_phone && <div><span className="text-muted-foreground">Alt Phone:</span><br/><span className="font-medium">{participant.alternate_phone}</span></div>}
                  <div><span className="text-muted-foreground">Email:</span><br/><span className="font-medium">{participant.email || '-'}</span></div>
                  <div><span className="text-muted-foreground">Address:</span><br/><span className="font-medium">{participant.address || '-'}</span></div>
                  <div><span className="text-muted-foreground">State:</span><br/><span className="font-medium">{participant.state_id || '-'}</span></div>
                  <div><span className="text-muted-foreground">Branch:</span><br/><span className="font-mono">{participant.branch_id || '-'}</span></div>
                  {/* Enhanced fields - Only for permitted roles */}
                  {accessControl.can_view_funding && (
                    <div><span className="text-muted-foreground">Funding Type:</span><br/><Badge variant="outline">{participant.funding_type ? participant.funding_type.replace(/_/g, ' ') : 'Not Set'}</Badge></div>
                  )}
                  {accessControl.can_view_funding && participant.plan_start_date && <div><span className="text-muted-foreground">Plan Period:</span><br/><span className="font-medium">{participant.plan_start_date} — {participant.plan_end_date || 'Ongoing'}</span></div>}
                  {participant.guardian_name && <div><span className="text-muted-foreground">Guardian:</span><br/><span className="font-medium">{participant.guardian_name}</span>{participant.guardian_phone && <span className="text-xs text-muted-foreground ml-1">{participant.guardian_phone}</span>}</div>}
                  {participant.primary_language && <div><span className="text-muted-foreground">Language:</span><br/><span className="font-medium">{participant.primary_language}{participant.interpreter_required && <Badge className="ml-1 text-[10px]" variant="destructive">Interpreter Required</Badge>}</span></div>}
                  {participant.mobility_support_required && <div><Badge variant="outline" className="text-xs">Mobility Support Required</Badge></div>}
                  {participant.transport_support_required && <div><Badge variant="outline" className="text-xs">Transport Support Required</Badge></div>}
                  {participant.cultural_notes && <div className="col-span-2 md:col-span-3"><span className="text-muted-foreground">Cultural Notes:</span><br/><span className="text-sm">{participant.cultural_notes}</span></div>}
                </div>
              </CardContent>
            </Card>
            {/* Profile Photo */}
            {participant.profile_photo_url && (
              <Card className="lg:row-span-2">
                <CardContent className="p-4 flex justify-center">
                  <AuthedImage 
                    src={participant.profile_photo_url} 
                    alt={`${participant.first_name}'s profile`}
                    className="w-32 h-32 rounded-full object-cover border-2"
                  />
                </CardContent>
              </Card>
            )}
            {!participant.profile_photo_url && (
              <AISummaryPanel 
                summaryType="PARTICIPANT" 
                entityId={participantId}
                entityType="PARTICIPANT"
                title="AI Summary"
              />
            )}
          </div>
          {participant.profile_photo_url && (
            <AISummaryPanel 
              summaryType="PARTICIPANT" 
              entityId={participantId}
              entityType="PARTICIPANT"
              title="AI Summary"
            />
          )}
            </>
          )}
          {/* Care Plan & Goals v2 — Phase 1.4: chart integration card. */}
          {dashboard?.care_and_goals && (
            <CareAndGoalsCard
              careAndGoals={dashboard.care_and_goals}
              onNavigate={(leafId) => handleLeafChange(leafId)}
            />
          )}
          {/* Phase 4.5: Outcomes Summary card on the Participant Chart.
              Mounts only when goal_outcomes_reporting_enabled is ON. */}
          {outcomesReportingFlagEnabled && (
            <OutcomeSummaryCard participantId={participantId} />
          )}

          {/* Phase 5: AI Progress Summary panel. The component itself reads
              the care_plan_ai_assist_enabled flag and renders nothing when OFF. */}
          <ProgressSummaryPanel participantId={participantId} />
          {/* V3: Inline Timeline Preview */}
          {profileV3Enabled && timelineV2Enabled && (
            <InlineTimelinePreview participantId={participantId} />
          )}
        </TabsContent>

        {/* ONBOARDING TAB — Phase 2F (Feb 2026)
            Renders the OnboardingChecklistPanel in full mode. Flag-gated by
            `participant_onboarding_checklist_enabled` (default OFF). Strictly
            informational — no row blocks any workflow. */}
        <TabsContent value="onboarding" className="space-y-4 mt-4" data-testid="participant-onboarding-tab">
          <OnboardingChecklistPanel participantId={participantId} mode="full" />
        </TabsContent>

        {/* ADDRESSES TAB (NEW) */}
        <TabsContent value="addresses" className="space-y-4 mt-4">
          <AddressesTab participantId={participantId} />
        </TabsContent>

        {/* Phase 2 — ACCOMMODATION: CURRENT RESIDENCE */}
        <TabsContent value="accommodation-current" className="space-y-4 mt-4">
          <CurrentResidenceTab participantId={participantId} mode="current" />
        </TabsContent>

        {/* Phase 2 — ACCOMMODATION: TENANCY HISTORY */}
        <TabsContent value="accommodation-history" className="space-y-4 mt-4">
          <CurrentResidenceTab participantId={participantId} mode="history" />
        </TabsContent>

        {/* Phase 2 — ACCOMMODATION: OVERLAY (feature-flag gated, fail-closed) */}
        <TabsContent value="accommodation-overlay" className="space-y-4 mt-4">
          <AccommodationOverlayTab participantId={participantId} />
        </TabsContent>

        {/* NDIS DETAILS TAB (NEW) */}
        <TabsContent value="ndis-details" className="space-y-4 mt-4">
          <NDISFundingDetailsTab participantId={participantId} onEditParticipant={canManageParticipant ? () => setEditParticipantOpen(true) : undefined} refreshToken={participant?.updated_at} />
        </TabsContent>

        {/* FUNDING TAB */}
        <TabsContent value="funding" className="space-y-4 mt-4">
          <ParticipantFundingReadinessPanel participantId={participantId} />
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Funding Plans</h2>
            <Dialog open={fundingDialog} onOpenChange={setFundingDialog}>
              <DialogTrigger asChild><Button size="sm" data-testid="create-funding-button"><Plus className="h-4 w-4 mr-1" /> New Plan</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create Funding Plan</DialogTitle><DialogDescription>Add a funding plan with NDIS categories</DialogDescription></DialogHeader>
                <form onSubmit={createFundingPlan} className="space-y-4">
                  <div className="space-y-2"><Label>Plan Name</Label><Input value={fpForm.plan_name} onChange={(e) => setFpForm({...fpForm, plan_name: e.target.value})} required data-testid="fp-name-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Start Date</Label><TrinitiDatePicker value={fpForm.start_date} onChange={(v) => setFpForm({...fpForm, start_date: v})} required testId="fp-start-date" /></div>
                    <div className="space-y-2"><Label>End Date</Label><TrinitiDatePicker value={fpForm.end_date} onChange={(v) => setFpForm({...fpForm, end_date: v})} required testId="fp-end-date" /></div>
                  </div>
                  <div className="border rounded-lg p-3 space-y-3">
                    <Label className="font-medium">Budget Categories</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1"><Label className="text-xs">CORE ($)</Label><Input type="number" step="0.01" value={fpForm.core} onChange={(e) => setFpForm({...fpForm, core: e.target.value})} placeholder="0" data-testid="fp-core-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">CAPACITY ($)</Label><Input type="number" step="0.01" value={fpForm.capacity} onChange={(e) => setFpForm({...fpForm, capacity: e.target.value})} placeholder="0" /></div>
                      <div className="space-y-1"><Label className="text-xs">CAPITAL ($)</Label><Input type="number" step="0.01" value={fpForm.capital} onChange={(e) => setFpForm({...fpForm, capital: e.target.value})} placeholder="0" /></div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="fp-submit-button">Create Plan</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Plans + Balances */}
          {fundingPlans.map(fp => (
            <Card key={fp.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> {fp.plan_name}</CardTitle>
                <p className="text-xs text-muted-foreground">{fp.start_date} to {fp.end_date}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-bold tabular-nums">${(fp.total_budget||0).toLocaleString()}</p>
                  </div>
                  {(fp.categories||[]).map(c => (
                    <div key={c.type} className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">{c.type}</p>
                      <p className="font-bold tabular-nums text-sm">${(c.remaining||0).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">of ${(c.allocated||0).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Ledger Table */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Transaction Ledger</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {transactions.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No transactions</TableCell></TableRow>
                  : transactions.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs">{t.created_at?.split('T')[0]}</TableCell>
                      <TableCell className="text-sm">{t.description}</TableCell>
                      <TableCell><StatusBadge status={t.category || 'CORE'} /></TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-destructive">-${(t.amount||0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUPPORT TEAM TAB (NEW) */}
        <TabsContent value="support-team" className="space-y-4 mt-4">
          <SupportTeamTab participantId={participantId} />
        </TabsContent>

        {/* PREFERENCES & RISKS TAB (NEW) */}
        <TabsContent value="preferences" className="space-y-4 mt-4">
          <PreferencesRisksTab participantId={participantId} />
        </TabsContent>

        {/* PORTAL ACCESS — logins to the participant portal (admins) */}
        <TabsContent value="portal-access" className="space-y-4 mt-4">
          <PortalAccessTab participantId={participantId} />
        </TabsContent>

        {/* CONSENTS TAB (NEW - Phase 5) */}
        <TabsContent value="consents" className="space-y-4 mt-4">
          <ConsentsTab participantId={participantId} />
        </TabsContent>

        {/* TASKS TAB */}
        <TabsContent value="tasks" className="space-y-4 mt-4">
          <ParticipantTasksTab participantId={participantId} />
        </TabsContent>

        {/* EXPIRING ITEMS TAB (NEW - Phase 5) */}
        <TabsContent value="expiring" className="space-y-4 mt-4">
          <ExpiringItemsTab participantId={participantId} />
        </TabsContent>

        {/* SERVICE ITEMS TAB */}
        <TabsContent value="services" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">Service Items</h2>
              {canScheduleHome && <button type="button" className="text-xs text-primary underline" onClick={() => handleLeafChange('schedules')} data-testid="services-open-schedule-home">Scheduling home → Schedule ▸ Schedules</button>}
            </div>
            <Dialog open={itemDialog} onOpenChange={setItemDialog}>
              <DialogTrigger asChild><Button size="sm" data-testid="create-service-item-button"><Plus className="h-4 w-4 mr-1" /> Add Service Item</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add Service Item</DialogTitle><DialogDescription>Create a recurring service schedule from a line of an ACTIVE service agreement (§8)</DialogDescription></DialogHeader>
                <form onSubmit={createServiceItem} className="space-y-4">
                  <div className="space-y-2"><Label>Agreement line <span className="text-red-500">*</span></Label>
                    {schedulableLines(agreementsForLines).length === 0 ? (
                      <p className="text-xs text-muted-foreground" data-testid="si-no-active-line">No ACTIVE service agreement with a schedulable line — create or activate an agreement first (§8). <button type="button" className="underline" onClick={() => { setItemDialog(false); handleLeafChange('agreements'); }}>Open Agreements</button></p>
                    ) : (
                      <Select value={siForm.line_key || ''} onValueChange={(v) => setSiForm({...siForm, line_key: v})}>
                        <SelectTrigger data-testid="si-line-select"><SelectValue placeholder="Select agreement line" /></SelectTrigger>
                        <SelectContent>{schedulableLines(agreementsForLines).map(l => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="space-y-2"><Label>Funding Plan</Label>
                    <Select value={siForm.funding_plan_id} onValueChange={(v) => setSiForm({...siForm, funding_plan_id: v})}>
                      <SelectTrigger data-testid="si-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
                      <SelectContent>{fundingPlans.map(f => <SelectItem key={f.id} value={f.id}>{f.plan_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Service Branch <span className="text-red-500">*</span></Label>
                    <Select value={siForm.branch_id} onValueChange={(v) => setSiForm({...siForm, branch_id: v})}>
                      <SelectTrigger data-testid="si-branch-select"><SelectValue placeholder="Select service branch" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name} — {b.effective_timezone}{b.timezone_source==='BRANCH'?'':' (inherited)'}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">Occurrences, presets and overnight boundaries use this branch's timezone.</p>
                  </div>
                  <div className="space-y-2"><Label>Typical Ratio <span className="text-muted-foreground font-normal">(informational — delivered ratio is set on the shift)</span></Label>
                    <Select value={siForm.support_ratio} onValueChange={(v) => setSiForm({...siForm, support_ratio: v})}>
                      <SelectTrigger data-testid="si-support-ratio-select"><SelectValue placeholder="1:1 planning default" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1:1">1:1 — one worker, one participant</SelectItem>
                        <SelectItem value="1:2">1:2 — one worker, two participants</SelectItem>
                        <SelectItem value="1:3">1:3 — one worker, three participants</SelectItem>
                        <SelectItem value="2:1">2:1 — two workers, one participant</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">Optional, informational only — a typical ratio for this service item. The ratio that is actually billed is the one set on each roster shift / participant allocation; this value never gates billing readiness.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Frequency</Label>
                      <Select value={siForm.uiFreq} onValueChange={(v) => setSiForm({...siForm, uiFreq: v})}>
                        <SelectTrigger data-testid="si-frequency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQUENCY_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {siForm.uiFreq === 'One-off' ? (
                      <div className="space-y-2"><Label>Date</Label>
                        <Input type="date" data-testid="si-one-off-date" value={siForm.one_off_date} onChange={(e) => setSiForm({...siForm, one_off_date: e.target.value})} />
                      </div>
                    ) : (
                      <div className="space-y-2"><Label>Recurrence start</Label>
                        <Input type="date" data-testid="si-recurrence-start" value={siForm.recurrence_start_date} onChange={(e) => setSiForm({...siForm, recurrence_start_date: e.target.value})} />
                      </div>
                    )}
                  </div>

                  {(siForm.uiFreq === 'Weekly' || siForm.uiFreq === 'Fortnightly') && (
                    <div className="space-y-2"><Label>Weekdays</Label>
                      <div className="flex flex-wrap gap-1" data-testid="si-weekdays">
                        {WEEKDAYS.map(w => {
                          const on = siForm.days_of_week.includes(w.value);
                          return (
                            <button type="button" key={w.value} data-testid={`si-weekday-${w.value}`}
                              onClick={() => setSiForm({...siForm, days_of_week: on ? siForm.days_of_week.filter(d => d !== w.value) : [...siForm.days_of_week, w.value]})}
                              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-input hover:bg-muted'}`}>
                              {w.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {(siForm.uiFreq === 'Monthly' || siForm.uiFreq === 'Quarterly') && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Monthly mode</Label>
                        <Select value={siForm.monthly_mode} onValueChange={(v) => setSiForm({...siForm, monthly_mode: v})}>
                          <SelectTrigger data-testid="si-monthly-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DAY_OF_MONTH">Day of month</SelectItem>
                            <SelectItem value="NTH_WEEKDAY">Nth weekday</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {siForm.monthly_mode === 'DAY_OF_MONTH' ? (
                        <div className="space-y-2"><Label>Day (1–31)</Label>
                          <Input type="number" min="1" max="31" data-testid="si-day-of-month" value={siForm.day_of_month} onChange={(e) => setSiForm({...siForm, day_of_month: e.target.value})} />
                        </div>
                      ) : (
                        <div className="space-y-2"><Label>Which weekday</Label>
                          <div className="flex gap-1">
                            <Select value={siForm.nth_n} onValueChange={(v) => setSiForm({...siForm, nth_n: v})}>
                              <SelectTrigger data-testid="si-nth-n"><SelectValue /></SelectTrigger>
                              <SelectContent>{NTH_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={siForm.nth_weekday} onValueChange={(v) => setSiForm({...siForm, nth_weekday: v})}>
                              <SelectTrigger data-testid="si-nth-weekday"><SelectValue /></SelectTrigger>
                              <SelectContent>{WEEKDAYS.map(w => <SelectItem key={w.value} value={String(w.value)}>{w.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2"><Label>Start</Label><TrinitiTimePicker value={siForm.start_time} onChange={(v) => setSiForm({...siForm, start_time: v})} testId="si-start-time" /></div>
                    <div className="space-y-2"><Label>End</Label><TrinitiTimePicker value={siForm.end_time} onChange={(v) => setSiForm({...siForm, end_time: v})} testId="si-end-time" /></div>
                    {siForm.uiFreq !== 'One-off' && (
                      <div className="space-y-2"><Label>End by (opt.)</Label>
                        <Input type="date" data-testid="si-recurrence-end" value={siForm.recurrence_end_date} onChange={(e) => setSiForm({...siForm, recurrence_end_date: e.target.value})} />
                      </div>
                    )}
                  </div>
                  <Button type="submit" className="w-full" data-testid="si-submit-button">Add Service Item</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <ParticipantScheduleRows items={serviceItems} generating={generating} genForm={genForm} setGenForm={setGenForm} genOpenFor={genOpenFor} setGenOpenFor={setGenOpenFor} onGenerate={generateShifts} canGenerate={(user?.permissions || []).includes('roster.manage')} />   {/* RP-3: ONE schedule table */}
            </CardContent>
          </Card>
        </TabsContent>

        {/* NOTES & DOCS — LANDING (flag-gated) */}
        {notesdocsLandingEnabled && (
          <TabsContent value="notesdocs-landing" className="space-y-4 mt-4" data-testid="notesdocs-landing-tab-content">
            <NotesDocsLanding
              participantId={participantId}
              onOpenLeaf={(leaf) => handleLeafChange(leaf)}
              onOpenParentTab={(tabId, subId) => {
                const next = new URLSearchParams(window.location.search);
                next.set('tab', tabId);
                if (subId) next.set('sub', subId);
                setSearchParams(next, { replace: false });
                setActiveLeaf(subId || tabId);
              }}
            />
          </TabsContent>
        )}

        {/* NOTES & DOCS — ASSESSMENTS (flag-gated read-INDEX) */}
        {notesdocsLandingEnabled && (
          <TabsContent value="assessments" className="space-y-4 mt-4" data-testid="assessments-tab-content">
            <AssessmentsIndexTab participantId={participantId} />
          </TabsContent>
        )}

        {/* NOTES & DOCS — REPORTS (flag-gated dual-section index) */}
        {notesdocsLandingEnabled && (
          <TabsContent value="reports" className="space-y-4 mt-4" data-testid="reports-tab-content">
            <ReportsIndexTab participantId={participantId} />
          </TabsContent>
        )}

        {/* NOTES & DOCS — DOCUMENT CATEGORIES (flag-gated read-only reference) */}
        {notesdocsLandingEnabled && (
          <TabsContent value="document-categories" className="space-y-4 mt-4" data-testid="document-categories-tab-content">
            <DocumentCategoriesReference />
          </TabsContent>
        )}

        {/* NOTES TAB (label rendered as "Progress Notes" when Notes & Docs landing flag ON) */}
        <TabsContent value="notes" className="space-y-4 mt-4">
          <NotesTab participantId={participantId} />
        </TabsContent>

        {/* HANDOVER NOTES TAB (Admin read-only participant handover history) */}
        <TabsContent value="handover" className="space-y-4 mt-4">
          <AdminHandoverTab participantId={participantId} />
        </TabsContent>

        {/* CARE & SUPPORT LANDING (Phase 1 — reduced scope, flag: participant_caresupport_landing_enabled) */}
        {caresupportLandingEnabled && (
          <TabsContent value="landing" className="space-y-4 mt-4">
            <CareSupportLanding
              participantId={participantId}
              onOpenLeaf={(leaf) => handleLeafChange(leaf)}
              onOpenDomain={(domainType) => {
                // Deep-link to Care Plans leaf with ?domain=<type> hint. Preserves
                // participant id, browser refresh and back-navigation via URL state.
                const next = new URLSearchParams(window.location.search);
                next.set('tab', 'care-support');
                next.set('sub', 'careplans');
                next.set('domain', domainType);
                setSearchParams(next, { replace: false });
                setActiveLeaf('careplans');
              }}
              featureFlags={{
                participant_careplan_domains_enabled: careplanDomainsEnabled,
                care_plan_v2_structured_matrix_enabled: structuredMatrixEnabled,
                goal_outcomes_reporting_enabled: outcomesReportingFlagEnabled,
              }}
            />
          </TabsContent>
        )}

        {/* CARE PLANS TAB */}
        <TabsContent value="careplans" className="space-y-4 mt-4">
          <CarePlansTab participantId={participantId} />
        </TabsContent>

        {/* AGREEMENTS TAB — Phase 2C (Feb 2026) */}
        <TabsContent value="agreements" className="space-y-4 mt-4">
          <ParticipantAgreementsTab participantId={participantId} onOpenSchedules={(agreementId) => { setScheduleFocusAgreementId(agreementId || null); handleLeafChange('schedules'); }} />   {/* RP-3 Option A */}
        </TabsContent>

        {/* CHARTS TAB (Day 1 foundation, feature-flag gated) */}
        {showChartsTab && (
          <TabsContent value="charts" className="space-y-4 mt-4">
            <ParticipantChartsTab participantId={participantId} />
          </TabsContent>
        )}

        {/* DOCUMENTS TAB (Day 2 Participant Documents, feature-flag gated) */}
        {showDocumentsTab && (
          <TabsContent value="documents" className="space-y-4 mt-4">
            <ParticipantDocumentsTab participantId={participantId} />
          </TabsContent>
        )}

        {/* RISK PROFILE TAB — Phase 2D PR-3 */}
        <TabsContent value="risk-profile" className="space-y-4 mt-4">
          <RiskProfilePanel participantId={participantId} mode="full" />
        </TabsContent>

        {/* RISKS TAB */}
        <TabsContent value="risks" className="space-y-4 mt-4">
          <RisksTab participantId={participantId} />
        </TabsContent>

        {/* GOALS TAB */}
        <TabsContent value="goals" className="space-y-4 mt-4">
          <GoalsTab participantId={participantId} />
        </TabsContent>

        {/* OUTCOMES TAB — Phase 4.5 (Goal Outcomes & Reporting).
            Flag-gated; only mounted when goal_outcomes_reporting_enabled is ON. */}
        {outcomesReportingFlagEnabled && (
          <TabsContent value="outcomes" className="space-y-4 mt-4" data-testid="participant-outcomes-tab-content">
            <ParticipantOutcomesTab participantId={participantId} />
          </TabsContent>
        )}


        {/* MEDICATIONS TAB */}
        <TabsContent value="medications" className="space-y-4 mt-4">
          <MedicationsTab participantId={participantId} />
        </TabsContent>

        {/* Phase 3 — CLINICAL: ALLERGIES */}
        <TabsContent value="allergies" className="space-y-4 mt-4">
          <AllergiesTab
            participantId={participantId}
            canManage={(user?.permissions || []).includes('participants.manage')}
          />
        </TabsContent>

        {/* Phase 3 — CLINICAL: HEALTH CONDITIONS */}
        <TabsContent value="health-conditions" className="space-y-4 mt-4">
          <HealthConditionsTab
            participantId={participantId}
            canManage={(user?.permissions || []).includes('participants.manage')}
          />
        </TabsContent>

        {/* CONTINUITY TAB */}
        <TabsContent value="continuity" className="space-y-4 mt-4">
          <ContinuityTab participantId={participantId} />
        </TabsContent>

        {/* CONTACTS TAB */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <ContactsTab participantId={participantId} />
        </TabsContent>

        {/* Phase 2 — COMMUNICATION: EMAIL LOG (read-only) */}
        <TabsContent value="email-log" className="space-y-4 mt-4">
          <EmailLogTab participantId={participantId} />
        </TabsContent>

        {/* Phase 2 — COMMUNICATION: SMS LOG (read-only, Twilio MOCKED) */}
        <TabsContent value="sms-log" className="space-y-4 mt-4">
          <SmsLogTab participantId={participantId} />
        </TabsContent>

        {/* Phase 2 — COMMUNICATION: ENGAGEMENT THREADS (read-only) */}
        <TabsContent value="engagement-threads" className="space-y-4 mt-4">
          <EngagementThreadsTab participantId={participantId} />
        </TabsContent>

        {/* SCHEDULE — SCHEDULES HOME (RP-3 Option B; flag-independent, permission-gated via child visibility) */}
        <TabsContent value="schedules" className="space-y-4 mt-4" data-testid="participant-schedules-tab-content">
          <ParticipantSchedulesTab participantId={participantId} perms={user?.permissions || []} fundingPlans={fundingPlans} branches={branches}
            focusAgreementId={scheduleFocusAgreementId} onFocusConsumed={() => setScheduleFocusAgreementId(null)} />
        </TabsContent>

        {/* SCHEDULE — LANDING (flag-gated) */}
        {scheduleLandingEnabled && (
          <TabsContent value="schedule-landing" className="space-y-4 mt-4" data-testid="participant-schedule-landing-tab-content">
            <ScheduleLanding
              participantId={participantId}
              onOpenLeaf={(leaf) => handleLeafChange(leaf)}
            />
          </TabsContent>
        )}

        {/* SCHEDULE — ROSTERS (flag-gated read-only) */}
        {scheduleLandingEnabled && (
          <TabsContent value="rosters" className="space-y-4 mt-4" data-testid="participant-rosters-tab-content">
            <ParticipantRostersTab participantId={participantId} />
          </TabsContent>
        )}

        {/* APPOINTMENTS TAB */}
        <TabsContent value="appointments" className="space-y-4 mt-4">
          <AppointmentsTab participantId={participantId} />
        </TabsContent>

        {/* GROUP SESSIONS TAB (Day 4) — labelled "Activities" when Schedule Landing flag ON */}
        <TabsContent value="group-sessions" className="space-y-4 mt-4">
          <GroupSessionsTab participantId={participantId} />
        </TabsContent>

        {/* SCHEDULE — TRANSPORT BOOKINGS (flag-gated read-only) */}
        {scheduleLandingEnabled && (
          <TabsContent value="transport" className="space-y-4 mt-4" data-testid="participant-transport-tab-content">
            <ParticipantTransportTab participantId={participantId} />
          </TabsContent>
        )}

        {/* SCHEDULE — CALENDAR (flag-gated read-only, client-side aggregation) */}
        {scheduleLandingEnabled && (
          <TabsContent value="calendar" className="space-y-4 mt-4" data-testid="participant-calendar-tab-content">
            <ParticipantCalendarTab participantId={participantId} />
          </TabsContent>
        )}

        {/* SCHEDULE — APPOINTMENT OUTCOMES (flag-gated read-only; distinct from Goal / Participant Outcomes) */}
        {scheduleLandingEnabled && (
          <TabsContent value="appointment-outcomes" className="space-y-4 mt-4" data-testid="participant-appointment-outcomes-tab-content">
            <ParticipantAppointmentOutcomesTab participantId={participantId} />
          </TabsContent>
        )}

        {/* FORMS TAB */}
        <TabsContent value="forms" className="space-y-4 mt-4">
          <FormsTab participantId={participantId} />
        </TabsContent>

        {/* TEAMS TAB */}
        <TabsContent value="teams" className="space-y-4 mt-4">
          <TeamsTab participantId={participantId} />
        </TabsContent>

        {/* DUPLICATES TAB (NEW - Phase 6) */}
        <TabsContent value="duplicates" className="space-y-4 mt-4">
          <DuplicateDetectionTab participantId={participantId} />
        </TabsContent>

        {/* LINKED MODULES TAB (NEW - FRD Section 6.12) */}
        <TabsContent value="linked-modules" className="space-y-4 mt-4">
          <LinkedModulesTab participantId={participantId} />
        </TabsContent>

        {/* TAGS TAB (NEW - FRD Section 10.11) */}
        <TabsContent value="tags" className="space-y-4 mt-4">
          <TagsTab participantId={participantId} />
        </TabsContent>

        {/* FLAGS TAB (NEW - FRD Section 10.5) */}
        <TabsContent value="flags" className="space-y-4 mt-4">
          <FlagsTab participantId={participantId} />
        </TabsContent>

        {/* TIMELINE TAB */}
        <TabsContent value="timeline" className="space-y-4 mt-4">
          {timelineV2Enabled ? (
            <ParticipantUnifiedTimeline participantId={participantId} />
          ) : (
            <TimelineEventsTab participantId={participantId} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
